/* eslint-disable jsx-a11y/alt-text */
import { Typography } from '@cbreone/core-ui';
import { externalTheme } from '@marketiq/marketiq-core-theme';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { debounce } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useReducer,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useResizeDetector } from 'react-resize-detector';
import { match } from 'react-router';
import { useHistory } from 'react-router-dom';
import { BAMWSO2ClientID, BAMWSO2SecretID } from '../../config';
import InteractiveSurveyContext from '../../hooks/InteractiveSurveyContext';
import LeftArrowIcon from '../../interactive/Icons/LeftArrowIcon';
import RightArrowIcon from '../../interactive/Icons/RightArrowIcon';
import MapStyles from '../../map/MapControls/MapStyles';
import {
  BAM_MAP_STYLES,
  BAM_MAP_STYLE_LIST,
  BAM_MAP_STYLE_SOURCES,
  preferredMapStyleKey,
} from '../../map/constants/MapStyles';
import { PropertyRouteParams } from '../../routes/interactive.routes';
import { getActiveMediaFilesForProperty } from '../../services/interactive/data-service';
import { Property, PropertyPatch, SurveyContact } from '../../types';
import {
  flattenMediaFilesWithModifiedFiles,
  surveyShortlistedOverride,
} from '../../utilities';
import * as mapUtil from '../../utilities/map';
import InteractivePropertyDetail from './InteractivePropertyDetail';
import InteractiveSurveyPropertyImageModal from './Gallery';
import useInteractiveAPI from '../../services/interactive/useInteractiveAPI';
import { BHPropertyFieldRule } from '../../types/bh-property-field-rule';
import PropertyAvailabilities from './PropertyAvailabilities';
import { AVAILABILITY_NAME_KEY, MULTI_FLOOR_SUITE_FIELD } from '../../data/constants';
import { getAddressDisplay } from '../../utilities/property';
import { CommentBtn } from '../../components/Buttons';
import { getPropertyValueForDisplay } from '../../utilities/property/propertyUtil';

const PROPERTY_PAGE_LAYOUT = {
  headerHeight: 125,
  mobileHeaderHeight: 170,
  tabletHeaderHeight: 160,
  globalNavigationHeight: 48,
  stickyContentGap: 24,
  heroHeightDesktop: 420,
  heroHeightWide: 420,
  heroHeightMobile: 320,
  mapHeightDesktop: 350,
  mapHeightMobile: 183,
  detailPaddingDesktop: 40,
  detailPaddingMobile: 6,
  maxOverviewImages: 3,
  maxOverviewSecondaryImages: 2,
} as const;

const PROPERTY_STICKY_TOP =
  PROPERTY_PAGE_LAYOUT.globalNavigationHeight +
  PROPERTY_PAGE_LAYOUT.headerHeight +
  PROPERTY_PAGE_LAYOUT.stickyContentGap;

const PROPERTY_STICKY_TOP_TABLET =
  PROPERTY_PAGE_LAYOUT.globalNavigationHeight +
  PROPERTY_PAGE_LAYOUT.tabletHeaderHeight +
  PROPERTY_PAGE_LAYOUT.stickyContentGap;

const PROPERTY_STICKY_TOP_MOBILE =
  PROPERTY_PAGE_LAYOUT.globalNavigationHeight +
  PROPERTY_PAGE_LAYOUT.mobileHeaderHeight +
  PROPERTY_PAGE_LAYOUT.stickyContentGap;

const getResponsiveStickyTop = () => {
  if (typeof window !== 'undefined') {
    const viewportWidth = window.innerWidth;
    if (viewportWidth <= 767) {
      return PROPERTY_STICKY_TOP_MOBILE;
    }

    if (viewportWidth >= 768 && viewportWidth <= 1199) {
      return PROPERTY_STICKY_TOP_TABLET;
    }
  }

  return PROPERTY_STICKY_TOP;
};

const PROPERTY_SECTION_IDS = {
  overview: 'overview-section',
  availabilities: 'availabilities-section',
  amenities: 'amenities-section',
  propertyDetails: 'property-details-section',
  location: 'location-section',
  attachments: 'attachments-section',
} as const;

const normalizeFieldIdentity = (value?: string) =>
  `${value || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');

const EXTRACTED_PROPERTY_DETAIL_FIELD_IDENTITIES = {
  locationTransport: ['locationtransport', 'locationandtransport'],
  specificationAmenities: [
    'specificationamenities',
    'specificationandamenities',
    'propertyinfoamenities',
    'propertyinfospecificationamenities',
    'propertyinfospecificationandamenities',
    'propertyinfopropertytypeamenities',
  ],
  overviewDescriptions: [
    'propertydescription',
    'propertydescriptionforreports',
    'locationdescription',
    'locationdescriptionforreports',
  ],
} as const;

const EXCLUDED_PROPERTY_DETAIL_FIELD_IDENTITIES = [
  ...EXTRACTED_PROPERTY_DETAIL_FIELD_IDENTITIES.locationTransport,
  ...EXTRACTED_PROPERTY_DETAIL_FIELD_IDENTITIES.specificationAmenities,
  ...EXTRACTED_PROPERTY_DETAIL_FIELD_IDENTITIES.overviewDescriptions,
];

type TransportIconKind =
  | 'metro'
  | 'train'
  | 'bus'
  | 'airport'
  | 'seaway'
  | 'highway'
  | 'time'
  | 'walk';

type TransportCardItem = {
  icon: TransportIconKind;
  lines: string[];
};

const TRANSPORT_KEYWORD_RULES: Array<{
  icon: TransportIconKind;
  pattern: RegExp;
}> = [
  { icon: 'metro', pattern: /\b(metro|subway|underground|tube)\b/i },
  { icon: 'bus', pattern: /\b(bus|coach)\b/i },
  { icon: 'train', pattern: /\b(train|rail|railway|tram|station)\b/i },
  { icon: 'seaway', pattern: /\b(seaway|sea|ferry|harbou?r|port|ship|boat)\b/i },
  { icon: 'airport', pattern: /\b(airport|airway|airways|flight|terminal)\b/i },
  { icon: 'highway', pattern: /\b(highway|motorway|freeway|expressway|road)\b/i },
  { icon: 'walk', pattern: /\b(walk|walking|pedestrian|foot)\b/i },
  { icon: 'time', pattern: /\b(seconds?|secs?|minutes?|mins?|hours?|hrs?)\b/i },
];

const TRANSPORT_DURATION_OR_DISTANCE_UNITS = [
  'seconds?',
  'secs?',
  'minutes?',
  'mins?',
  'hours?',
  'hrs?',
  'miles?',
  'mile',
  'kilometers?',
  'kilometres?',
  'km',
  'feet',
  'foot',
  'ft',
].join('|');

const TRANSPORT_SEGMENT_SEPARATOR_PATTERN = new RegExp(
  `\\s+(?=\\d+(?:\\.\\d+)?\\s*(?:${TRANSPORT_DURATION_OR_DISTANCE_UNITS})\\b)`,
  'gi',
);

const COMMON_AMENITY_PHRASES = [
  'Electrical Cabling',
  'Fibre Optic',
  'Fiber Optic',
  'Openable Windows',
  'Raised Floors',
  'Suspended Ceilings',
  'Computer Cabling',
  'Air-Conditioning',
  'Air Conditioning',
  'Beauty & Wellness',
  'Outdoor Space',
  'Coffee Corner',
  'Hairstylist',
  'Auditorium',
  'Pick-Up',
  'Restaurant',
  'Conference Rooms',
  'Meeting Center',
  'Bike Sharing',
  'Dentist',
  'Openable',
  'Radiators with Convector Casing',
  'Year Engineering Cooling',
  'Climatic Ceiling',
  'Fitness Studio',
  'Reception',
];

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripHtmlToText = (value: string) =>
  `${value || ''}`
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<\/li>\s*<li[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');

const normalizeDisplayText = (value: string) =>
  stripHtmlToText(value).replace(/\s+/g, ' ').trim();

const getCommonAmenityPhraseItems = (value: string) => {
  const normalizedValue = normalizeDisplayText(value);
  if (!normalizedValue) {
    return [];
  }

  const phraseMatches = COMMON_AMENITY_PHRASES
    .map((phrase) => {
      const match = normalizedValue.match(new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'i'));
      return match?.index === undefined
        ? null
        : { item: match[0], index: match.index, end: match.index + match[0].length };
    })
    .filter(
      (match): match is { item: string; index: number; end: number } =>
        Boolean(match),
    )
    .sort(
      (first, second) =>
        first.index - second.index || second.item.length - first.item.length,
    );

  const nonOverlappingMatches = phraseMatches.reduce(
    (matches, currentMatch) => {
      const previousMatch = matches[matches.length - 1];

      if (!previousMatch || currentMatch.index >= previousMatch.end) {
        return [...matches, currentMatch];
      }

      return matches;
    },
    [] as Array<{ item: string; index: number; end: number }>,
  );

  return nonOverlappingMatches.map((match) => match.item);
};

const getDisplayListItems = (value?: string) => {
  const textWithBreaks = stripHtmlToText(value || '').replace(/\r/g, '');
  const delimitedItems = textWithBreaks
    .split(/\n|;|\||•|·|,/)
    .map((item) => normalizeDisplayText(item))
    .filter(Boolean);

  if (delimitedItems.length > 1) {
    return delimitedItems;
  }

  const commonAmenityItems = getCommonAmenityPhraseItems(textWithBreaks);
  if (commonAmenityItems.length > 1) {
    return commonAmenityItems;
  }

  if (delimitedItems.length === 1) {
    return delimitedItems;
  }

  const normalizedText = normalizeDisplayText(textWithBreaks);
  return normalizedText ? [normalizedText] : [];
};

const getTransportIconKind = (label: string): TransportIconKind =>
  TRANSPORT_KEYWORD_RULES.find((rule) => rule.pattern.test(label))?.icon || 'time';

const getTransportCardItems = (value?: string): TransportCardItem[] => {
  const rawText = stripHtmlToText(value || '').replace(/\r/g, '').trim();
  if (!rawText) {
    return [];
  }

  const hasLineBreaks = rawText.includes('\n') || rawText.includes('\\n');
  const normalizedLineBreaks = rawText.replace(/\\n/g, '\n');

  const parsedLines = (
    hasLineBreaks
      ? normalizedLineBreaks.split('\n')
      : normalizedLineBreaks
        .replace(TRANSPORT_SEGMENT_SEPARATOR_PATTERN, '\n')
        .split('\n')
  )
    .map((label) => normalizeDisplayText(label))
    .filter(Boolean);

  const groupedByIcon = new Map<TransportIconKind, string[]>();
  const iconOrder: TransportIconKind[] = [];

  parsedLines.forEach((line) => {
    const icon = getTransportIconKind(line);

    if (!groupedByIcon.has(icon)) {
      groupedByIcon.set(icon, []);
      iconOrder.push(icon);
    }

    groupedByIcon.get(icon)?.push(line);
  });

  return iconOrder.map((icon) => ({
    icon,
    lines: groupedByIcon.get(icon) || [],
  }));
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: theme.palette.grey['200'],
      display: 'flex',
      height: PROPERTY_PAGE_LAYOUT.headerHeight,
      width: '100%',
      position: 'fixed',
      '@media (max-width: 767px)': {
        height: `${PROPERTY_PAGE_LAYOUT.mobileHeaderHeight}px`,
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        height: `${PROPERTY_PAGE_LAYOUT.tabletHeaderHeight}px`,
      },
    },
    subtitle: {
      backgroundColor: theme.palette.grey['600'],
      display: 'flex',
      height: '40px',
      width: '100%',
    },
    subtitleText: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      color: theme.palette.primary.contrastText,
      display: 'flex',
      letterSpacing: '-0.01em',
      lineHeight: '20px',
      alignItems: 'center',
      paddingLeft: '24px',
    },
    headerText: {
      color: theme.palette.primary.main,
      paddingTop: 28,
      display: 'block',
      marginTop: 0,
      minWidth: 0,
      '@media (max-width: 767px)': {
        paddingTop: 14,
      },
      [theme.breakpoints.only('md')]: {
        paddingTop: 28,
      },
      [theme.breakpoints.only('sm')]: {
        paddingTop: 28,
      },
    },
    outerSpace: {
      padding: `${PROPERTY_PAGE_LAYOUT.detailPaddingDesktop}px`,
      width: '100vw',
      alignSelf: 'start',
      '@media (max-width: 767px)': {
        padding: `${PROPERTY_PAGE_LAYOUT.detailPaddingMobile}px`,
      },
    },
    availabilities: {
      background: '#fff',
      padding: '40px 50px 20px 50px',

      '@media(max-width:1279px)': {
        padding: '40px 0px 20px 32px',
      },
      '@media(max-width:767px)': {
        padding: '40px 0px 20px 6px',
      },
    },
    title: {
      fontFamily: 'Financier Display, Georgia, serif',
      fontSize: '34px',
      fontWeight: 400,
      lineHeight: '42px',
      letterSpacing: 0,
      [theme.breakpoints.only('md')]: {
        fontSize: '30px',
      },
      [theme.breakpoints.only('sm')]: {
        fontSize: '24px',
      },
      '@media (max-width: 767px)': {
        fontSize: '18px',
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        fontSize: '28px',
        lineHeight: '34px',
      },
      '@media (min-width: 1200px) and (max-width: 1439px)': {
        fontSize: '30px',
        lineHeight: '38px',
      },
    },
    addressText: {
      fontFamily: 'Calibre-R',
      fontSize: '18px',
      lineHeight: '26px',
      letterSpacing: 0,
      margin: 0,
      minWidth: 0,
      overflowWrap: 'anywhere',
      '@media (max-width: 767px)': {
        fontSize: '14px',
        lineHeight: '20px',
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        fontSize: '16px',
        lineHeight: '22px',
      },
      '@media (min-width: 1200px) and (max-width: 1439px)': {
        fontSize: '16px',
        lineHeight: '22px',
      },
    },
    headerAddressRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '6px',
      marginTop: '4px',
      maxWidth: '100%',
      color: theme.palette.text.primary,
    },
    headerAddressIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '22px',
      height: '22px',
      color: theme.palette.grey['700'],
      flexShrink: 0,
      '& svg': {
        width: '22px',
        height: '22px',
      },
      '@media (max-width: 767px)': {
        width: '18px',
        height: '18px',
        '& svg': {
          width: '18px',
          height: '18px',
        },
      },
    },
    headerShowMapButton: {
      border: 0,
      padding: 0,
      margin: 0,
      backgroundColor: 'transparent',
      color: 'var(--primary, #003F2D)',
      cursor: 'pointer',
      fontFamily: 'var(--font-family-primary, Calibre-R, system-ui, sans-serif)',
      fontSize: 'inherit',
      fontWeight: 500,
      lineHeight: 'inherit',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: 'var(--primary, #003F2D)',
      },
      '@media (max-width: 767px)': {
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: '32px',
      },
    },
    headerSecondaryText: {
      fontFamily: 'Calibre-R',
      fontSize: '24px',
      letterSpacing: 0,
      lineHeight: '32px',
      paddingBottom: 0,
      margin: 0,
      '@media (max-width: 767px)': {
        fontSize: '20px',
        lineHeight: '26px',
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        fontSize: '22px',
        lineHeight: '30px',
      },
    },
    paginationButton: {
      width: '50px',
      height: '50px',
      minWidth: '50px',
      padding: 0,
      borderRadius: '50%',
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '& .MuiButton-label': {
        width: '50px',
        height: '50px',
      },
      '& svg': {
        width: '50px',
        height: '50px',
        fontSize: '50px',
      },
      '@media (max-width: 767px)': {
        width: '44px',
        height: '44px',
        minWidth: '44px',
        '& .MuiButton-label': {
          width: '44px',
          height: '44px',
        },
        '& svg': {
          width: '44px',
          height: '44px',
          fontSize: '44px',
        },
      },
    },
    navigation: {
      paddingRight: 40,
      '@media (max-width: 767px)': {
        paddingRight: 0,
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        paddingRight: 12,
      },
    },
    innerNavigation: {
      paddingTop: 24,
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '10px',
      whiteSpace: 'nowrap',
      '@media (max-width: 767px)': {
        gap: '8px',
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        gap: '6px',
      },
    },
    indexCount: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '24px',
      color: theme.palette.text.primary,
      minWidth: '52px',
      textAlign: 'center',
      '@media (max-width: 767px)': {
        fontSize: '12px',
        lineHeight: '18px',
        minWidth: '32px',
        padding: 0,
      },
    },
    order: {
      color: theme.palette.secondary.light,
      paddingTop: 18,
      display: 'flex',
      paddingRight: 20,
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      '@media (max-width: 767px)': {
        paddingTop: 14,
        paddingRight: 10,
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        paddingTop: 16,
      },
    },
    propHeaderTitle: {
      [theme.breakpoints.only('md')]: {
        paddingLeft: 47,
      },
      [theme.breakpoints.only('sm')]: {
        paddingLeft: 67,
      },
      [theme.breakpoints.only('lg')]: {
        paddingLeft: 20,
      },
    },
    verticalLine: {
      borderLeft: `1px solid ${theme.palette.grey['800']}`,
      height: '80px',
      marginLeft: '18px',
      '@media (max-width: 767px)': {
        height: '60px',
        marginLeft: '12px',
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        height: '68px',
      },
    },
    imageStyle: {
      width: '100%',
      height: `${PROPERTY_PAGE_LAYOUT.heroHeightDesktop}px`,
      maxHeight: 'none',
      objectFit: 'cover',
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      cursor: 'pointer',
      transition: 'transform 400ms ease-in-out',
      willChange: 'transform',
      '&:hover': {
        transform: 'scale(1.08)',
      },
      [theme.breakpoints.only('md')]: {
        height: `${PROPERTY_PAGE_LAYOUT.heroHeightDesktop}px`,
      },
      [theme.breakpoints.only('sm')]: {
        height: '220px',
        width: '100%',
      },
      '@media (max-width: 767px)': {
        height: '220px',
        width: '100%',
      },
      '@media (min-width: 1200px)': {
        height: `${PROPERTY_PAGE_LAYOUT.heroHeightWide}px`,
      },
    },
    smallImageStyle: {
      height: '360px',
      width: '100%',
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      cursor: 'pointer',
      filter: 'contrast(1.2) saturate(1.08)',
    },
    secondaryImageStyle: {
      height: '320px',
      width: '100%',
      backgroundPositionX: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      cursor: 'pointer',
      position: 'relative',
      filter: 'contrast(1.2) saturate(1.08)',
      transition: 'transform 400ms ease-in-out',
      willChange: 'transform',
      '&:hover': {
        transform: 'scale(1.08)',
      },
      [theme.breakpoints.only('md')]: {
        height: '258px',
      },
      [theme.breakpoints.only('sm')]: {
        height: '258px',
        width: '259px',
      },
      '@media (max-width: 767px)': {
        height: '160px',
      },
    },
    imageStyleShape: {
      [theme.breakpoints.only('md')]: {
        paddingRight: 0,
      },
      [theme.breakpoints.only('sm')]: {
        paddingRight: 0,
      },
      [theme.breakpoints.only('lg')]: {
        paddingRight: 2,
      },
    },
    arrow: {
      width: '48px',
      height: '48px',
    },
    content: {
      flexGrow: 1,
      width: '100%',

      '@global': {
        '.custom-popover': {
          display: 'none !important',
        },
      },
    },
    pageTypographyScope: {
      fontFamily: 'Calibre-R, system-ui, sans-serif',
      '& .MuiTypography-body1': {
        fontSize: '14px',
        fontStyle: 'normal',
        fontFamily: 'Calibre-R, system-ui, sans-serif',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      '& .MuiButton-label': {
        width: '100%',
        display: 'inherit',
        alignItems: 'inherit',
        justifyContent: 'inherit',
        fontFamily: 'Calibre-R, system-ui, sans-serif',
        fontSize: '16px',
        lineHeight: '24px',
      },
    },
    propertyDetailContainer: {
      position: 'relative',
      background: '#fff',
      paddingTop: PROPERTY_PAGE_LAYOUT.headerHeight,
      '@media (max-width: 767px)': {
        paddingTop: `${PROPERTY_PAGE_LAYOUT.mobileHeaderHeight}px`,
      },
      '@media (max-width: 1199px) and (min-width: 768px)': {
        paddingTop: `${PROPERTY_PAGE_LAYOUT.tabletHeaderHeight}px`,
      },
    },
    overflowEffect: {
      position: 'relative',
      overflowY: 'auto',
    },
    imgListBox: {
      alignSelf: 'start',
      backgroundColor: '#FFFFFF',
    },

    publishShareModalSection: {
      textAlign: 'right',
      color: externalTheme.palette.primary.contrastText,
      backgroundColor: externalTheme.palette.primary.main,
      textTransform: 'none',
      minWidth: '143px',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '1.5',
      '&:hover': {
        backgroundColor: externalTheme.palette.primary.main,
      },
      '@media (max-width: 767px)': {
        minWidth: '100%',
        height: '100%',
        borderRadius: 0,
      },
    },
    buttonGrid: {
      display: 'inline',
    },

    mobileBox: {
      display: 'none',
      '@media (max-width: 767px)': {
        margin: '1px',
        display: 'flex',
        justifyContent: 'right',
      },
    },
    desktopBox: {
      display: 'block',
      '@media (max-width: 767px)': {
        display: 'none',
      },
    },
    propertyDetailText: {
      color: theme.palette.primary.main,
      paddingBottom: '16px',
    },
    commentsAmenitiesHeaders: {
      color: theme.palette.primary.main,
      paddingBottom: '16px',
      paddingTop: '40px',
    },
    commentsAmenitiesFonts: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '24px',
    },
    propertyList: {
      color: theme.palette.text.primary,
    },
    overlay: {
      position: 'absolute',
      bottom: 0,
      minHeight: '13%',
      backgroundColor: '#435354',
      opacity: '90%',
      color: '#fff',
      paddingLeft: '24px',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
    },
    flyerBox: {
      backgroundColor: 'rgba(0,63,45,0.1)',
      borderRadius: '16px',
      width: 'fit-content',
      margin: '3px',
      padding: '4px 10px 1px 10px',
      height: '32px',
      float: 'left',
    },
    flyerDiv: {
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      paddingLeft: '25px',
      paddingBottom: '2px',
      cursor: 'pointer',
    },
    attachmentCardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '16px',
      marginTop: '14px',
      '@media (max-width: 767px)': {
        gridTemplateColumns: '1fr',
      },
    },
    attachmentCard: {
      display: 'grid',
      gridTemplateColumns: '34px minmax(0, 1fr)',
      columnGap: '14px',
      alignItems: 'center',
      minHeight: '82px',
      padding: '16px',
      border: '1px solid #DFE6E5',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      color: theme.palette.text.primary,
      textDecoration: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 160ms ease, box-shadow 160ms ease',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: '0 6px 18px rgba(0, 63, 45, 0.08)',
        textDecoration: 'none',
      },
    },
    attachmentFileIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: '4px',
      color: '#FFFFFF',
      fontFamily: 'Calibre-R',
      fontSize: '10px',
      fontWeight: 700,
      lineHeight: '12px',
      textTransform: 'uppercase',
    },
    attachmentFileIconPdf: {
      backgroundColor: '#C8102E',
    },
    attachmentFileIconSheet: {
      backgroundColor: '#006A4D',
    },
    attachmentCardTitle: {
      display: 'block',
      fontFamily: 'Calibre-R',
      fontSize: '18px',
      lineHeight: '24px',
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    attachmentCardMeta: {
      display: 'block',
      marginTop: '4px',
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '20px',
      color: '#5F7E90',
    },
    icon: {
      color: externalTheme.palette.primary.main,
      verticalAlign: 'middle',
      marginLeft: '20px',
      '@media (max-width: 767px)': {
        position: 'fixed',
        bottom: '75px',
        right: '20px',
        backgroundColor: '#003F2D',
        borderRadius: '4px',
        width: '30px',
        height: '30px',
        padding: '5px',
        color: '#FFFFFF',
      },
    },
    iconMobile: {
      color: externalTheme.palette.primary.main,
      verticalAlign: 'middle',
      marginLeft: '7px',
      marginTop: '7px',
      marginRight: '2px',
      '@media (min-width: 766px)': {
        position: 'fixed',
        bottom: '75px',
        right: '20px',
        backgroundColor: '#003F2D',
        borderRadius: '4px',
        width: '30px',
        height: '30px',
        padding: '5px',
        color: '#FFFFFF',
      },
    },
    shortlisted: {
      textAlign: 'right',
      color: externalTheme.palette.primary.contrastText,
      backgroundColor: externalTheme.palette.primary.main,
      textTransform: 'none',
      width: '82px',
      height: '24px',
      fontSize: '10px',
      fontWeight: 400,
      lineHeight: '16px',
      position: 'absolute',
      padding: '0px -8px 0px 0px',
      bottom: '60px',
      '& .MuiChip-label': {
        paddingLeft: '8px',
      },
      '&:focus': {
        backgroundColor: externalTheme.palette.primary.main,
      },
      '&:hover': {
        backgroundColor: externalTheme.palette.primary.main,
      },
    },
    shortlist: {
      textAlign: 'right',
      color: externalTheme.palette.primary.main,
      backgroundColor: externalTheme.palette.grey[900],
      textTransform: 'none',
      width: '82px',
      height: '24px',
      fontSize: '10px',
      fontWeight: 500,
      lineHeight: '16px',
      borderRadius: 16,
      position: 'absolute',
      bottom: '60px',
      '& .MuiChip-label': {
        paddingLeft: '8px',
      },
      '&:hover': {
        backgroundColor: externalTheme.palette.grey[900],
      },
      '&:focus': {
        backgroundColor: externalTheme.palette.grey[900],
      },
    },
    chip: {
      width: '10.26px',
      height: '7.82px',
      backgroundColor: externalTheme.palette.primary.main,
      color: externalTheme.palette.primary.contrastText,
    },
    chipShortList: {
      width: '12px',
      height: '12px',
      backgroundColor: externalTheme.palette.grey[900],
      color: externalTheme.palette.primary.main,
    },
    chipRoot: {
      width: '200px',
      '@media (max-width: 767px)': {
        width: '96px',
        marginBottom: '20px',
      },
    },
    paddingBottom125: {
      '@media (max-width: 767px)': {
        paddingBottom: '125px',
      },
    },
    overviewActionRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '8px 8px 0 8px',
      '@media (max-width: 767px)': {
        padding: '8px 0 0 0',
      },
    },
    viewAllButton: {
      textTransform: 'none',
      minWidth: '128px',
      height: '40px',
      padding: '0 14px',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '3px',
      backgroundColor: theme.palette.common.white,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '20px',
      fontWeight: 500,
      boxShadow: 'none',
      '& .MuiButton-label': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        whiteSpace: 'nowrap',
      },
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#F5F7F7',
        boxShadow: 'none',
      },
      '@media (max-width: 767px)': {
        width: '100%',
      },
    },
    heroSection: {
      width: '100%',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',
    },
    heroPrimaryColumn: {
      paddingRight: '2px',
      overflow: 'hidden',
      position: 'relative',
      '@media (max-width: 767px)': {
        paddingRight: 0,
      },
    },
    imageLoaderOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      zIndex: 1,
    },
    heroSecondaryColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      height: `${PROPERTY_PAGE_LAYOUT.heroHeightDesktop}px`,
      overflow: 'hidden',
      '@media (min-width: 1200px)': {
        height: `${PROPERTY_PAGE_LAYOUT.heroHeightWide}px`,
      },
    },
    heroSecondaryItem: {
      flex: 1,
      display: 'block',
      width: '100%',
      minHeight: 0,
      border: 0,
      padding: 0,
      cursor: 'pointer',
      transition: 'transform 400ms ease-in-out',
      willChange: 'transform',
      '&:disabled': {
        cursor: 'default',
      },
      '&:hover': {
        transform: 'scale(1.08)',
      },
    },
    heroMapTile: {
      position: 'relative',
      display: 'block',
      width: '100%',
      border: 0,
      padding: 0,
      cursor: 'pointer',
      color: theme.palette.primary.main,
      backgroundColor: '#F0F3F3',
      backgroundImage: `
        linear-gradient(90deg, transparent 0, transparent 46%, #D8DEDE 47%, #D8DEDE 49%, transparent 50%),
        linear-gradient(0deg, transparent 0, transparent 46%, #D8DEDE 47%, #D8DEDE 49%, transparent 50%)
      `,
      backgroundSize: '120px 72px',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'rgba(245, 247, 247, 0.76)',
      },
    },
    heroMapPreviewTile: {
      position: 'relative',
      display: 'block',
      width: '100%',
      border: 0,
      padding: 0,
      cursor: 'pointer',
      overflow: 'hidden',
      backgroundColor: '#E7ECEC',
    },
    heroMapPreviewCanvas: {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      '& mapping-component': {
        display: 'block',
        width: '100%',
        height: '100%',
      },
    },
    heroMapPreviewBadge: {
      position: 'absolute',
      left: '12px',
      bottom: '12px',
      zIndex: 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '7px 10px',
      borderRadius: '3px',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '18px',
      textDecoration: 'underline',
    },
    heroMapContent: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      height: '100%',
      minHeight: '56px',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '20px',
      textDecoration: 'underline',
    },
    heroViewAllOverlay: {
      position: 'absolute',
      right: '16px',
      bottom: '16px',
      zIndex: 1,
      '@media (max-width: 767px)': {
        position: 'relative',
        right: 'auto',
        bottom: 'auto',
        marginTop: '8px',
      },
    },
    contentColumns: {
      position: 'relative',
      width: '100%',
      backgroundColor: '#FFFFFF',
      padding: '50px 0 32px 0',
      '@media (max-width: 767px)': {
        padding: '28px 16px 120px 16px',
      },
    },
    contentGrid: {
      display: 'grid !important',
      gridTemplateColumns:
        'clamp(190px, 18vw, 226px) minmax(0, 1fr) clamp(340px, 30vw, 408px)',
      columnGap: 'clamp(16px, 2vw, 24px)',
      alignItems: 'start',
      width: '100%',
      margin: '0 auto',
      '& > .MuiGrid-item': {
        maxWidth: 'none',
        flexBasis: 'auto',
        width: 'auto',
      },
      '@media (min-width: 1200px)': {
        gridTemplateColumns: '226px minmax(0, 1fr) 408px',
        columnGap: '24px',
        width: '100%',
      },
      '@media (max-width: 1023px)': {
        display: 'block !important',
        width: '100%',
        padding: '0 20px',
      },
      '@media (max-width: 767px)': {
        padding: 0,
      },
    },
    navColumn: {
      paddingRight: 0,
      paddingLeft: '20px',
      boxSizing: 'border-box',
      '@media (min-width: 1200px)': {
        paddingLeft: '32px',
      },
      '@media (min-width: 1024px)': {
        position: 'sticky',
        top: `${PROPERTY_STICKY_TOP}px`,
        alignSelf: 'flex-start',
      },
      '@media (min-width: 1024px) and (max-width: 1199px)': {
        top: `${PROPERTY_STICKY_TOP_TABLET}px`,
      },
      '@media (max-width: 767px)': {
        paddingRight: 0,
        paddingLeft: 0,
      },
    },
    mainColumn: {
      paddingRight: 0,
      minWidth: 0,
      '@media (max-width: 767px)': {
        paddingRight: 0,
      },
    },
    sideColumn: {
      paddingRight: '20px',
      boxSizing: 'border-box',
      width: '100%',
      '@media (min-width: 1200px)': {
        paddingRight: '32px',
      },
      '@media (min-width: 1024px)': {
        position: 'sticky',
        top: `${PROPERTY_STICKY_TOP}px`,
        alignSelf: 'flex-start',
        justifySelf: 'end',
        zIndex: 3,
      },
      '@media (min-width: 1024px) and (max-width: 1199px)': {
        top: `${PROPERTY_STICKY_TOP_TABLET}px`,
      },
      '@media (max-width: 1023px)': {
        marginTop: '32px',
        paddingRight: 0,
      },
    },
    sectionPaper: {
      backgroundColor: '#E6EAEA',
      borderRadius: 0,
      border: 'none',
      boxSizing: 'border-box',
      padding: '30px 28px',
      marginBottom: '16px',
      minHeight: 'auto',
      width: '100%',
      minWidth: 'clamp(320px, 30vw, 390px)',
      maxWidth: '420px',
      marginLeft: 'auto',
      '@media (min-width: 1200px)': {
        minWidth: '380px',
      },
      '@media (max-width: 767px)': {
        padding: '20px',
        minHeight: 'auto',
        minWidth: '0',
        maxWidth: 'none',
      },
    },
    sectionNavContainer: {
      marginBottom: '24px',
      width: '100%',
      '@media (max-width: 767px)': {
        marginTop: '8px',
        marginBottom: '16px',
      },
    },
    sectionNavItem: {
      display: 'block',
      width: '100%',
      marginBottom: '8px',
      color: '#778F9C',
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '20px',
      textDecoration: 'none',
      textAlign: 'left',
      maxWidth: '100%',
      paddingLeft: '14px',
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      borderTop: 0,
      borderRight: 0,
      borderBottom: 0,
      borderLeft: '2px solid transparent',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: theme.palette.primary.main,
        textDecoration: 'none',
      },
      '@media (max-width: 767px)': {
        fontSize: '18px',
        lineHeight: '24px',
        whiteSpace: 'normal',
      },
    },
    sectionNavItemActive: {
      borderLeftColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontWeight: 600,
    },
    sectionBlock: {
      marginBottom: '40px',
      scrollMarginTop: `${PROPERTY_STICKY_TOP + 16}px`,
      animation: '$sectionFadeIn 500ms ease-out both',
      '@media (max-width: 1199px) and (min-width: 768px)': {
        scrollMarginTop: `${PROPERTY_STICKY_TOP_TABLET + 16}px`,
      },
    },
    attachmentsBlock: {
      marginBottom: '56px',
    },
    '@keyframes sectionFadeIn': {
      from: {
        opacity: 0,
        transform: 'translateY(18px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    sectionHeading: {
      color: theme.palette.primary.main,
      fontFamily: 'Financier Display, Georgia, serif',
      fontSize: '28px',
      fontWeight: 400,
      lineHeight: '36px',
      margin: '0 0 16px',
      whiteSpace: 'normal',
    },
    descriptionLabel: {
      color: theme.palette.primary.main,
      fontFamily: 'Financier Display, Georgia, serif',
      fontSize: '28px',
      fontWeight: 400,
      lineHeight: '36px',
      letterSpacing: 0,
      marginBottom: '24px',
      whiteSpace: 'normal',
      '@media (max-width: 767px)': {
        fontSize: '22px',
      },
    },
    descriptionBody: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '24px',
      color: '#555550',
      whiteSpace: 'pre-line',
    },
    descriptionSubTitle: {
      color: theme.palette.text.primary,
      fontFamily: 'Calibre-R',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '26px',
      marginBottom: '14px',
      '@media (max-width: 767px)': {
        fontSize: '18px',
      },
    },
    descriptionAction: {
      textTransform: 'none',
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontWeight: 600,
      fontSize: '12px !important',
      marginTop: '8px',
      paddingLeft: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    requestViewingButton: {
      width: '100%',
      textTransform: 'none',
      minHeight: '56px',
      marginBottom: '28px',
      paddingLeft: '18px',
      paddingRight: '18px',
      backgroundColor: '#003F2D',
      color: '#FFFFFF',
      fontFamily: 'Calibre-R, system-ui, sans-serif',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '24px',
      borderRadius: '4px',
      boxShadow: 'none',
      whiteSpace: 'normal',
      textAlign: 'center',
      '&:hover': {
        backgroundColor: '#003F2D',
        boxShadow: 'none',
      },
      '& .MuiButton-label': {
        lineHeight: '24px',
      },
      '@media (max-width: 767px)': {
        fontSize: '16px',
      },
    },
    sidebarActionRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) 56px',
      gap: '10px',
      alignItems: 'center',
      marginBottom: '38px',
    },
    shortlistPropertyButton: {
      width: '100%',
      minHeight: '56px',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '4px',
      color: theme.palette.primary.main,
      backgroundColor: '#FFFFFF',
      textTransform: 'none',
      fontFamily: 'Calibre-R',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '22px',
      whiteSpace: 'normal',
      boxShadow: 'none',
      paddingLeft: '12px',
      paddingRight: '12px',
      '&:hover': {
        backgroundColor: '#FFFFFF',
        boxShadow: '0 8px 18px rgba(0, 63, 45, 0.12)',
      },
      '& .MuiButton-label': {
        justifyContent: 'center',
        textAlign: 'center',
        whiteSpace: 'normal',
      },
      '& .MuiButton-startIcon': {
        minWidth: '18px',
        marginRight: '8px',
      },
    },
    shortlistedPropertyButton: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    commentActionButton: {
      '& button': {
        width: '56px',
        minWidth: '56px',
        height: '56px',
        padding: 0,
        margin: 0,
        borderRadius: '4px',
        boxShadow: 'none',
        backgroundColor: '#FFFFFF',
      },
      '& svg': {
        width: '22px !important',
        height: '22px !important',
        margin: '0 !important',
      },
    },
    advisorPanel: {
      border: 'none',
      borderRadius: 0,
      backgroundColor: 'transparent',
      padding: 0,
      marginBottom: 0,
      maxWidth: '296px',
    },
    advisorHeading: {
      color: theme.palette.primary.main,
      fontFamily: 'Financier Display, Georgia, serif',
      fontSize: '28px',
      fontWeight: 400,
      lineHeight: '36px',
      marginBottom: '24px',
    },
    advisorList: {
      display: 'flex',
      flexDirection: 'column',
    },
    advisorListScrollable: {
      maxHeight: '540px',
      overflowY: 'auto',
      paddingRight: '6px',
      '@media (max-width: 1199px) and (min-width: 768px)': {
        maxHeight: '500px',
      },
      '@media (max-width: 767px)': {
        maxHeight: '460px',
      },
    },
    advisorRow: {
      display: 'flex',
      alignItems: 'flex-start',
      paddingBottom: '18px',
      marginBottom: '18px',
      borderBottom: '1px solid rgba(95, 126, 144, 0.16)',
      '&:last-child': {
        marginBottom: 0,
        paddingBottom: 0,
        borderBottom: 'none',
      },
    },
    advisorAvatar: {
      width: '42px',
      height: '42px',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Calibre-R',
      fontWeight: 600,
      fontSize: '16px',
      marginRight: '14px',
      flexShrink: 0,
    },
    advisorInfo: {
      minWidth: 0,
      flex: 1,
    },
    advisorName: {
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '26px',
      marginBottom: '6px',
      '@media (max-width: 767px)': {
        fontSize: '20px',
        lineHeight: '28px',
      },
    },
    advisorTitle: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '24px',
      color: theme.palette.text.primary,
      marginBottom: '4px',
    },
    advisorMetaGroup: {
      display: 'grid',
      rowGap: '4px',
    },
    advisorMeta: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '24px',
      color: theme.palette.text.primary,
      display: 'block',
      textDecoration: 'none',
      wordBreak: 'break-word',
      '&:hover': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
      },
    },
    detailsTableSection: {
      '& table': {
        width: '100%',
      },
    },
    extractedFieldBlock: {
      marginTop: '14px',
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '24px',
      color: theme.palette.text.primary,
      '& p': {
        marginTop: 0,
        marginBottom: 0,
        fontFamily: 'Calibre-R',
        fontSize: '16px',
        lineHeight: '24px',
      },
      '& ul': {
        marginTop: 0,
        marginBottom: 0,
        paddingLeft: '18px',
      },
    },
    amenitiesList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '8px 20px',
      listStyle: 'none',
      margin: '8px 0 0 0',
      padding: 0,
      maxWidth: '100%',
      '@media (max-width: 767px)': {
        gridTemplateColumns: '1fr',
      },
    },
    amenityListItem: {
      display: 'grid',
      gridTemplateColumns: '18px minmax(0, 1fr)',
      columnGap: '6px',
      alignItems: 'start',
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '22px',
      color: 'rgb(26, 26, 26)',
      overflowWrap: 'anywhere',
    },
    amenityDash: {
      color: '#7A7A7A',
      lineHeight: '22px',
    },
    locationMapWrapper: {
      paddingTop: '12px',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '6px',
    },
    locationMapWrapperExpanded: {
      position: 'fixed',
      inset: 0,
      width: '100vw',
      height: '100vh',
      zIndex: theme.zIndex.modal + 10,
      paddingTop: 0,
      backgroundColor: '#FFFFFF',
      borderRadius: 0,
      boxShadow: 'none',
    },
    locationMapContentExpanded: {
      height: '100%',
    },
    locationMapModal: {
      position: 'fixed',
      inset: 0,
      zIndex: theme.zIndex.modal + 100,
      width: '100vw',
      height: '100vh',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',
    },
    locationMapModalContent: {
      position: 'relative',
      width: '100vw',
      height: '100vh',
      backgroundColor: '#FFFFFF',
      outline: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    locationMapModalHeader: {
      flex: '0 0 74px',
      minHeight: '74px',
      padding: '0 22px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
      borderBottom: '1px solid #EEF2F2',
    },
    locationMapModalTitle: {
      margin: 0,
      paddingRight: '16px',
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '22px',
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    locationMapModalBody: {
      position: 'relative',
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
    },
    mapControlGroup: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: '6px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
    },
    mapControlGroupExpanded: {
      top: '18px',
      right: '22px',
    },
    mapControlButton: {
      width: '34px',
      height: '34px',
      minWidth: '34px',
      padding: 0,
      borderRadius: 0,
      border: 0,
      backgroundColor: '#FFFFFF',
      color: theme.palette.primary.main,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#F5F7F7',
      },
      '& + &': {
        borderTop: '1px solid #DFE6E5',
      },
      '& svg': {
        width: '16px',
        height: '16px',
      },
    },
    mapExpandButton: {
      position: 'absolute',
      right: '16px',
      bottom: '18px',
      zIndex: 3,
      width: '34px',
      height: '34px',
      minWidth: '34px',
      padding: 0,
      borderRadius: '6px',
      backgroundColor: '#FFFFFF',
      color: '#5A5A5A',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
      '&:hover': {
        backgroundColor: '#F5F7F7',
      },
      '& svg': {
        width: '16px',
        height: '16px',
      },
      '@media (max-width: 767px)': {
        right: '12px',
        bottom: '12px',
      },
    },
    mapCloseButton: {
      width: '44px',
      height: '44px',
      minWidth: '44px',
      padding: 0,
      borderRadius: 0,
      backgroundColor: '#FFFFFF',
      color: theme.palette.text.primary,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#F5F7F7',
      },
      '& svg': {
        width: '22px',
        height: '22px',
      },
    },
    getDirectionsButton: {
      position: 'absolute',
      left: '16px',
      bottom: '18px',
      zIndex: 2,
      minHeight: '46px',
      padding: '10px 18px',
      borderRadius: '4px',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      textTransform: 'none',
      fontFamily: 'Calibre-R',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '24px',
      boxShadow: '0 6px 18px rgba(0, 63, 45, 0.18)',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        backgroundColor: '#B9C4C4',
        color: '#FFFFFF',
        boxShadow: 'none',
      },
      '& .MuiButton-startIcon': {
        marginRight: '8px',
      },
      '& svg': {
        width: '22px',
        height: '22px',
      },
      '@media (max-width: 767px)': {
        left: '12px',
        bottom: '12px',
        minHeight: '40px',
        padding: '8px 14px',
        fontSize: '16px',
        lineHeight: '20px',
      },
    },
    transportGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))',
      gap: '12px',
      marginTop: '16px',
      maxWidth: '100%',
    },
    transportTile: {
      minHeight: '122px',
      backgroundColor: '#F1F4F4',
      borderRadius: '4px',
      padding: '16px 20px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      color: theme.palette.text.primary,
    },
    transportTilePlaceholder: {
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '86px',
    },
    transportIcon: {
      width: '28px',
      height: '28px',
      color: '#5A5A5A',
      marginBottom: '10px',
      flexShrink: 0,
      '& svg': {
        width: '28px',
        height: '28px',
      },
    },
    transportText: {
      fontFamily: 'Calibre-R',
      fontSize: '16px',
      lineHeight: '22px',
      color: theme.palette.text.primary,
      margin: 0,
      overflowWrap: 'anywhere',
    },
    transportTextLine: {
      display: 'block',
      '& + &': {
        marginTop: '4px',
      },
    },
    availabilitySection: {
      maxWidth: '100%',
      overflowX: 'auto',
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch',
      '& .MuiTableContainer-root': {
        maxWidth: '100%',
      },
      '& .MuiPaper-root': {
        maxWidth: '100%',
      },
    },
  }),
);

interface PropertyImages {
  displayCaption?: string;
  id?: number;
  url: string;
  mediaType: string;
  pdf?: string;
  downloadUrl?: string;
  displayName?: string;
  origin?: string;
  fileSize?: number | string;
}

type Props = {
  match: match;
};

type PropertySidebarActionsProps = {
  propertyId: string;
  isClientShortlist: boolean;
  onShortlistClick: () => void;
  shortlistLabel: string;
  shortlistedLabel: string;
  classes: Record<string, string>;
};

type ExpandableDescriptionBlockProps = {
  title: string;
  description: string;
  emptyState: string;
  viewMoreLabel: string;
  showLessLabel: string;
  titleClassName: string;
  textClassName: string;
  actionClassName: string;
};

const DESCRIPTION_COLLAPSED_LINE_COUNT = 3;
const DESCRIPTION_LINE_HEIGHT = 24;
const DESCRIPTION_COLLAPSED_HEIGHT =
  DESCRIPTION_COLLAPSED_LINE_COUNT * DESCRIPTION_LINE_HEIGHT;

const ExpandableDescriptionBlock: React.FC<ExpandableDescriptionBlockProps> = ({
  title,
  description,
  emptyState,
  viewMoreLabel,
  showLessLabel,
  titleClassName,
  textClassName,
  actionClassName,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hasLongText, setHasLongText] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const hasDescription = Boolean(description);
  const shouldFadeText = hasLongText && !expanded;

  useEffect(() => {
    const descriptionElement = descriptionRef.current;

    if (!descriptionElement || !hasDescription) {
      setHasLongText(false);
      return undefined;
    }

    const updateOverflowState = () => {
      if (!expanded) {
        setHasLongText(
          descriptionElement.scrollHeight > descriptionElement.clientHeight + 1,
        );
      }
    };

    updateOverflowState();
    window.addEventListener('resize', updateOverflowState);

    return () => window.removeEventListener('resize', updateOverflowState);
  }, [description, expanded, hasDescription]);

  return (
    <Box style={{ marginBottom: '24px' }}>
      <Typography variant="body2" className={titleClassName}>
        {title}
      </Typography>
      <div
        ref={descriptionRef}
        style={{
          maxHeight:
            hasDescription && !expanded ? DESCRIPTION_COLLAPSED_HEIGHT : 'none',
          overflow: hasDescription && !expanded ? 'hidden' : 'visible',
          position: 'relative',
        }}
      >
        <Typography className={textClassName}>
          {description || emptyState}
        </Typography>
        {shouldFadeText && (
          <Box
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 88%)',
              bottom: 0,
              height: '34px',
              left: 0,
              pointerEvents: 'none',
              position: 'absolute',
              right: 0,
            }}
          />
        )}
      </div>
      {hasLongText && (
        <Button
          className={actionClassName}
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {expanded ? showLessLabel : viewMoreLabel}
        </Button>
      )}
    </Box>
  );
};

const CalendarActionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect
      x="4"
      y="5"
      width="14"
      height="13"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M8 3v4M14 3v4M4 9h14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect x="8" y="12" width="2" height="2" fill="currentColor" />
    <rect x="12" y="12" width="2" height="2" fill="currentColor" />
  </svg>
);

const PlusActionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M11 4.5v13M4.5 11h13"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
    />
  </svg>
);

const CheckActionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M5 11.5l4 4L17.5 6"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MapPinActionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M11 19s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z"
      fill="white"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <circle cx="11" cy="8" r="2.25" fill="currentColor" />
  </svg>
);

const DirectionsActionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M4 11.5 18 4l-4.8 14-2.4-6.1L4 11.5Z"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
    <path
      d="m10.8 11.9 7.2-7.9"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </svg>
);

const ZoomInIcon = () => (
  <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M11 5v12M5 11h12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ZoomOutIcon = () => (
  <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M5 11h12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ExpandMapIcon = () => (
  <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M8 4H4v4M14 4h4v4M8 18H4v-4M14 18h4v-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseMapIcon = () => (
  <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M6 6l10 10M16 6 6 16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ViewAllImagesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="3"
      width="12"
      height="12"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M5.25 12.75 8.1 9.9a1 1 0 0 1 1.42 0l.74.74a1 1 0 0 0 1.42 0l.42-.42a1 1 0 0 1 1.42 0l1.48 1.48" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="6.75" cy="6.75" r="1" fill="currentColor" />
  </svg>
);

const MetroIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <rect
      x="6"
      y="4"
      width="16"
      height="17"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M9 9h10M9 14h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="10" cy="18" r="1.5" fill="currentColor" />
    <circle cx="18" cy="18" r="1.5" fill="currentColor" />
    <path d="M10 24l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrainIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <rect
      x="6"
      y="3"
      width="16"
      height="18"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M10 7h8M9 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="10" cy="17" r="1.5" fill="currentColor" />
    <circle cx="18" cy="17" r="1.5" fill="currentColor" />
    <path d="M9 25l3-4M19 25l-3-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BusIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <rect
      x="5"
      y="5"
      width="18"
      height="15"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M8 10h12M8 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="9" cy="20" r="1.5" fill="currentColor" />
    <circle cx="19" cy="20" r="1.5" fill="currentColor" />
    <path d="M8 24v-3M20 24v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const AirportIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path
      d="M4 15l20-8-7 17-4-7-7 3-2-5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M13 17l11-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SeawayIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M7 15h14l-3 6H10l-3-6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 15V8h6l3 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 23c2 1.4 4 1.4 6 0s4-1.4 6 0 4 1.4 6 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const HighwayIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M10 24l2-20M18 24L16 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 7v3M14 14v3M14 21v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 24h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TimeIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M14 8v6l4 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WalkIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="15" cy="5" r="2" fill="currentColor" />
    <path d="M14 9l-3 6 5 2 3 6M12 15l-3 8M15 10l5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TransportIcon = ({ icon }: { icon: TransportIconKind }) => {
  switch (icon) {
    case 'metro':
      return <MetroIcon />;
    case 'train':
      return <TrainIcon />;
    case 'bus':
      return <BusIcon />;
    case 'airport':
      return <AirportIcon />;
    case 'seaway':
      return <SeawayIcon />;
    case 'highway':
      return <HighwayIcon />;
    case 'walk':
      return <WalkIcon />;
    case 'time':
    default:
      return <TimeIcon />;
  }
};

const PropertySidebarActions: React.FC<PropertySidebarActionsProps> = ({
  propertyId,
  isClientShortlist,
  onShortlistClick,
  shortlistLabel,
  shortlistedLabel,
  classes,
}) => (
  <Box className={classes.sidebarActionRow}>
    <Button
      className={`${classes.shortlistPropertyButton} ${
        isClientShortlist ? classes.shortlistedPropertyButton : ''
      }`}
      onClick={onShortlistClick}
      startIcon={isClientShortlist ? <CheckActionIcon /> : <PlusActionIcon />}
    >
      {isClientShortlist ? shortlistedLabel : shortlistLabel}
    </Button>
    <Box className={classes.commentActionButton}>
      <CommentBtn myId={propertyId} />
    </Box>
  </Box>
);

const mediumPlaceholderImg = '/assets/images/image-placeholder-medium.png';

const InteractiveSurveyProperty: React.FC<Props> = ({ match: { params } }) => {
  const { surveyId, propertyId } = params as PropertyRouteParams;
  const { t } = useTranslation();
  const { survey } = useContext(InteractiveSurveyContext);
  const [propertyList, setPropertyList] = useState<Property[]>([]);
  const [images, setImages] = useState<PropertyImages[]>([]);
  const [floorplan, setFloorplan] = useState<PropertyImages[]>([]);
  const [flier, setFlier] = useState<PropertyImages[]>([]);
  const { width, ref } = useResizeDetector();
  const [isCommentVisible, setIsCommentVisible] = useState<boolean>(false);
  const [reducerValue, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isAmenitiesVisible, setIsAmenitiesVisible] = useState<boolean>(false);
  const [activeAvailabilityRules, setActiveAvailabilityRules] =
    useState<any[]>();
  const [shortlisted, setShortlisted] = useState(
    localStorage.getItem('shortlisted') === 'true',
  );
  const [isCESurvey, setIsCESurvey] = useState<boolean>(false);
  const [isLocationMapExpanded, setIsLocationMapExpanded] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const propertyMapHeight = isDesktop
    ? PROPERTY_PAGE_LAYOUT.mapHeightDesktop
    : PROPERTY_PAGE_LAYOUT.mapHeightMobile;
  const propertyMapDisplayHeight = isLocationMapExpanded
    ? window.innerHeight
    : propertyMapHeight;
  const propertyMapDisplayWidth = isLocationMapExpanded
    ? window.innerWidth
    : width;
  const [activeSectionId, setActiveSectionId] = useState<string>(
    PROPERTY_SECTION_IDS.overview,
  );
  const [currentMapStyle, setCurrentMapStyle] = useState<string>(() => {
    const storedStyle = localStorage.getItem(preferredMapStyleKey);
    if (storedStyle && BAM_MAP_STYLE_LIST.includes(storedStyle)) {
      return storedStyle;
    }
    if (survey?.marketMapStyle && BAM_MAP_STYLE_LIST.includes(survey.marketMapStyle)) {
      return survey.marketMapStyle;
    }
    return BAM_MAP_STYLES.cbreLight;
  });
  const { updateMapPoints } = mapUtil.default;
  const imgBoxRef = useRef<any>(null);
  const propertyDetailsRef = useRef<any>(null);
  const requestedSectionIdRef = useRef<string | null>(null);

  const { updateInteractiveProperty } = useInteractiveAPI(surveyId);
  const history = useHistory();
  const property = useMemo(() => {
    if (survey?.properties) {
      return survey.properties.find((item: any) => item.id === propertyId);
    }
    return {} as Property;
  }, [propertyId, survey]);

  const inlineMapCompRef = useRef<any>();
  const expandedMapCompRef = useRef<any>();
  const overviewMapCompRef = useRef<any>();
  const classes = useStyles();

  const changeArrowBackground = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'none';
  };

  useEffect(() => {
    const storedStyle = localStorage.getItem(preferredMapStyleKey);
    if (!storedStyle || !BAM_MAP_STYLE_LIST.includes(storedStyle)) {
      if (survey?.marketMapStyle && BAM_MAP_STYLE_LIST.includes(survey.marketMapStyle)) {
        setCurrentMapStyle(survey.marketMapStyle);
      } else {
        setCurrentMapStyle(BAM_MAP_STYLES.cbreLight);
      }
    }
  }, [survey?.marketMapStyle, survey?.id]);

  const applyStyleWithRetry = useCallback((mapComponent: any, mapSource: string, attempt = 0) => {
    if (!mapComponent?.updateMapStyles) {
      return;
    }

    try {
      mapComponent.updateMapStyles(mapSource);
    } catch {
      if (attempt < 3) {
        window.setTimeout(() => applyStyleWithRetry(mapComponent, mapSource, attempt + 1), 200);
      }
    }
  }, []);

  const applyMapStyle = useCallback((style: string) => {
    const mapSource = BAM_MAP_STYLE_SOURCES[style] || BAM_MAP_STYLE_SOURCES[BAM_MAP_STYLES.cbreLight];
    const activeMap = isLocationMapExpanded ? expandedMapCompRef?.current : inlineMapCompRef?.current;
    const inactiveMap = isLocationMapExpanded ? inlineMapCompRef?.current : expandedMapCompRef?.current;
    const overviewMap = overviewMapCompRef?.current;

    applyStyleWithRetry(activeMap, mapSource);

    if (inactiveMap) {
      window.setTimeout(() => applyStyleWithRetry(inactiveMap, mapSource), 100);
    }

    if (overviewMap) {
      window.setTimeout(() => applyStyleWithRetry(overviewMap, mapSource), 120);
    }
  }, [isLocationMapExpanded, applyStyleWithRetry]);

  const applyLatestMapStyle = useCallback(() => {
    const latestStyle = localStorage.getItem(preferredMapStyleKey);
    applyMapStyle(
      latestStyle && BAM_MAP_STYLE_LIST.includes(latestStyle)
        ? latestStyle
        : currentMapStyle,
    );
  }, [applyMapStyle, currentMapStyle]);

  const onMapStyleChange = useCallback((style: string) => {
    setCurrentMapStyle(style);
    localStorage.setItem(preferredMapStyleKey, style);
    applyMapStyle(style);
  }, [applyMapStyle]);

  useEffect(() => {
    setClickedMedia(null);
    if (property?.latitude && property?.longitude) {
      const targetRef = isLocationMapExpanded ? expandedMapCompRef : inlineMapCompRef;
      updateMapPoints([property], targetRef, survey, '', 15, 1, applyLatestMapStyle);
    }
  }, [property, propertyId, updateMapPoints, survey, applyLatestMapStyle, isLocationMapExpanded]);

  useEffect(() => {
    const activeMapRef = isLocationMapExpanded ? expandedMapCompRef : inlineMapCompRef;
    if (activeMapRef?.current && activeMapRef.current.mapResize) {
      activeMapRef.current.mapResize(propertyMapDisplayWidth);
    }
  }, [propertyMapDisplayWidth, isLocationMapExpanded]);

  useEffect(() => {
    const activeMapRef = isLocationMapExpanded ? expandedMapCompRef : inlineMapCompRef;

    if (activeMapRef?.current && activeMapRef.current.mapResize) {
      window.setTimeout(() => activeMapRef.current.mapResize(propertyMapDisplayWidth), 120);
    }

    if (property?.latitude && property?.longitude) {
      window.setTimeout(() => {
        updateMapPoints([property], activeMapRef, survey, '', 15, 1, applyLatestMapStyle);
      }, 180);
    }
  }, [isLocationMapExpanded, propertyMapDisplayWidth, property, survey, applyLatestMapStyle]);

  const setOverviewMapRef = useCallback((node: any) => {
    if (!node) {
      return;
    }

    overviewMapCompRef.current = node;

    if (property?.latitude && property?.longitude) {
      updateMapPoints(
        [property as Property],
        overviewMapCompRef,
        survey,
        '',
        15,
        1,
        applyLatestMapStyle,
      );

      // The map web component can initialize style state asynchronously.
      // Apply the preferred style again on short delays to match the Location map preview.
      const latestStyle = localStorage.getItem(preferredMapStyleKey);
      const resolvedStyle =
        latestStyle && BAM_MAP_STYLE_LIST.includes(latestStyle)
          ? latestStyle
          : currentMapStyle;
      const mapSource =
        BAM_MAP_STYLE_SOURCES[resolvedStyle] ||
        BAM_MAP_STYLE_SOURCES[BAM_MAP_STYLES.cbreLight];

      window.setTimeout(() => applyStyleWithRetry(node, mapSource), 250);
      window.setTimeout(() => applyStyleWithRetry(node, mapSource), 650);
    }
  }, [
    property,
    survey,
    updateMapPoints,
    applyLatestMapStyle,
    currentMapStyle,
    applyStyleWithRetry,
  ]);

  useEffect(() => {
    document.body.style.overflow = isLocationMapExpanded ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isLocationMapExpanded]);

  useEffect(() => {
    if (survey?.properties) {
      const activePropertyList = survey.properties.filter(
        (item: Property) =>
          !item.metaData?.isCurrentLocation &&
          surveyShortlistedOverride(shortlisted, item),
      );
      setPropertyList(activePropertyList);
    }

    const availabilityRules =
      survey?.surveyPropertyAvailabilityRule
        ?.filter(
          (rule) => {
            if (!isCESurvey && MULTI_FLOOR_SUITE_FIELD === rule.fieldKey && rule.status === 'ACTIVE') {
              setIsCESurvey(true);
            }
            return rule.status === 'ACTIVE' && rule.fieldKey !== AVAILABILITY_NAME_KEY;
          },
        )
        .sort((first: any, second: any) => first.order - second.order) || [];
    setActiveAvailabilityRules(availabilityRules);
    if (survey && survey.surveyPropertyFieldRule) {
      const isCommentAvailable = survey.surveyPropertyFieldRule.find(
        (rule: any) => rule.status === 'ACTIVE' && rule.fieldKey === 'comments',
      );

      const isAmenitiesAvailable = survey.surveyPropertyFieldRule.find(
        (rule: any) =>
          rule.status === 'ACTIVE' && rule.fieldKey === 'amenities',
      );
      setIsCommentVisible(!!isCommentAvailable);
      setIsAmenitiesVisible(!!isAmenitiesAvailable);
    }
  }, [survey, reducerValue]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [clickedMedia, setClickedMedia] = React.useState<any>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [imagesFetched, setImageFetched] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [secondaryImagesLoading, setSecondaryImagesLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setImageFetched(false);
    setImageLoaded(false);
    setSecondaryImagesLoading({});
    if (imgBoxRef?.current) {
      imgBoxRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    const getImagesForProperty = async () => {
      const response: any = await getActiveMediaFilesForProperty(
        surveyId,
        propertyId,
        'IMAGES',
      );
      const propertyImages: PropertyImages[] = response.media.map(
        (propertyImg: any) => ({
          id: propertyImg.mediaId,
          url: propertyImg.cloudfrontUrl,
          mediaType: propertyImg.mediaType,
          displayName: propertyImg.displayName,
          displayCaption: propertyImg.displayCaption,
        }),
      );
      setImages(propertyImages);
      return propertyImages;
    };

    const getFloorplanForProperty = async () => {
      const response: any = await getActiveMediaFilesForProperty(
        surveyId,
        propertyId,
        'FLOORPLANS',
      );
      const propertyFloorplan: PropertyImages[] = response.media.map(
        (propertyImg: any) => {
          if (propertyImg?.displayName?.includes('.pdf')) {
            return {
              id: propertyImg.mediaId,
              url: propertyImg.pdfImageUrl,
              mediaType: propertyImg.mediaType,
              pdf: propertyImg.cloudfrontUrl,
              downloadUrl: propertyImg.cloudfrontUrl,
              displayName: propertyImg.displayName,
              displayCaption: propertyImg.displayCaption,
              origin: propertyImg.origin ? propertyImg.origin : 'sc',
              fileSize: propertyImg.fileSize || propertyImg.size,
            };
          }
          return {
            id: propertyImg.mediaId,
            url: propertyImg.cloudfrontUrl,
            mediaType: propertyImg.mediaType,
            downloadUrl: propertyImg.cloudfrontUrl,
            displayName: propertyImg.displayName,
            displayCaption: propertyImg.displayCaption,
            origin: propertyImg.origin ? propertyImg.origin : 'sc',
            fileSize: propertyImg.fileSize || propertyImg.size,
          };
        },
      );
      setFloorplan(propertyFloorplan);
      return propertyFloorplan;
    };

    const getFlierForProperty = async () => {
      const response: any = await getActiveMediaFilesForProperty(
        surveyId,
        propertyId,
        'FLIERS',
      );
      const mediaFiles = flattenMediaFilesWithModifiedFiles(response.media);

      const propertyFlier: PropertyImages[] = mediaFiles.map(
        (propertyImg: any) => ({
          id: propertyImg.mediaId,
          url: propertyImg.pdfImageUrl,
          mediaType: propertyImg.mediaType,
          pdf: propertyImg.cloudfrontUrl,
          downloadUrl: propertyImg.cloudfrontUrl,
          displayName: propertyImg.displayName,
          displayCaption: propertyImg.displayCaption,
          fileSize: propertyImg.fileSize || propertyImg.size,
        }),
      );
      setFlier(propertyFlier);
      return propertyFlier;
    };

    (async () => {
      let propertyImage = await getImagesForProperty();
      if (!survey.isIncludePhotos && propertyImage) {
        propertyImage = [propertyImage[0]];
      }
      if (survey.isIncludeSitePlans) {
        await getFloorplanForProperty();
      }
      if (survey.isIncludeFlyers) {
        await getFlierForProperty();
      }
      setImageFetched(!!property?.id);
    })();
  }, [
    property,
    propertyId,
    survey.isIncludeFlyers,
    survey.isIncludePhotos,
    survey.isIncludeSitePlans,
    surveyId,
  ]);

  // Preload secondary images and track loading state
  const preloadSecondaryImage = (imageId: string, imageUrl: string) => {
    if (secondaryImagesLoading[imageId] === false) {
      return; // Already marked as loaded
    }
    const img = new Image();
    img.onload = () => {
      setSecondaryImagesLoading((prev) => ({ ...prev, [imageId]: false }));
    };
    img.onerror = () => {
      setSecondaryImagesLoading((prev) => ({ ...prev, [imageId]: false }));
    };
    setSecondaryImagesLoading((prev) => ({ ...prev, [imageId]: true }));
    img.src = imageUrl;
  };

  useEffect(() => {
    function onResize() {
      setIsDesktop(window.innerWidth >= 768);
      calculateContainerHeights();
    }

    onResize();

    window.addEventListener('resize', debounce(onResize, 100));

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [
    imageLoaded,
    imagesFetched,
    imgBoxRef?.current,
    propertyDetailsRef?.current,
  ]);

  const calculateContainerHeights = useCallback(() => {
    if (imagesFetched && imgBoxRef?.current && propertyDetailsRef?.current) {
      imgBoxRef.current.style.height = 'auto';
      propertyDetailsRef.current.style.height = 'auto';

      const imgBoxHeight = imgBoxRef.current.offsetHeight;
      const detailsHeight = propertyDetailsRef.current.offsetHeight;

      const maxHeight =
        window.innerHeight - (PROPERTY_PAGE_LAYOUT.headerHeight + 48);

      if (maxHeight < imgBoxHeight || maxHeight < detailsHeight) {
        if (maxHeight < imgBoxHeight) {
          imgBoxRef.current.style.height = `${maxHeight}px`;
        }
        if (maxHeight < detailsHeight) {
          propertyDetailsRef.current.style.height = `${maxHeight}px`;
        }
      }
    }
  }, [imagesFetched, imgBoxRef?.current, propertyDetailsRef?.current]);

  const addressDisplay: string = property ? getAddressDisplay(property, survey.surveyCountry?.trim()) : '';
  const propertyLatitude = Number(property?.latitude);
  const propertyLongitude = Number(property?.longitude);
  const hasValidPropertyCoordinates =
    Number.isFinite(propertyLatitude) &&
    Number.isFinite(propertyLongitude) &&
    Math.abs(propertyLatitude) <= 90 &&
    Math.abs(propertyLongitude) <= 180;

  const openPropertyDirections = () => {
    if (!hasValidPropertyCoordinates) {
      return;
    }

    const destination = encodeURIComponent(
      `${propertyLatitude},${propertyLongitude}`,
    );
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const updatePropertyMapZoom = async (direction: 'in' | 'out') => {
    const mapComponent = (isLocationMapExpanded ? expandedMapCompRef : inlineMapCompRef)?.current;

    if (!mapComponent) {
      return;
    }

    if (direction === 'in' && typeof mapComponent.zoomIn === 'function') {
      mapComponent.zoomIn();
      return;
    }

    if (direction === 'out' && typeof mapComponent.zoomOut === 'function') {
      mapComponent.zoomOut();
      return;
    }

    if (
      typeof mapComponent.getMapZoom === 'function' &&
      typeof mapComponent.updateMapZoom === 'function'
    ) {
      const currentZoom = await mapComponent.getMapZoom();
      mapComponent.updateMapZoom(
        Number(currentZoom || 0) + (direction === 'in' ? 1 : -1),
      );
    }
  };

  const getPropertyOrder = () => {
    if (survey?.properties) {
      return survey.properties
        .filter(
          (item: Property) =>
            !item.metaData?.isCurrentLocation &&
            surveyShortlistedOverride(shortlisted, item),
        )
        .sort((first: Property, second: Property) => first.order - second.order)
        .findIndex((item: Property) => item.id === propertyId);
    }
    return 1;
  };

  const nextProperty = () => {
    if (survey?.properties && propertyList.length > 0) {
      const order = getPropertyOrder() + 1;
      const { id } = propertyList[order] || undefined;
      history.push(`/${surveyId}/property/${id}`);
    }
  };

  const previousProperty = () => {
    if (survey?.properties) {
      const order = getPropertyOrder() - 1;
      const { id } = propertyList[order];
      history.push(`/${surveyId}/property/${id}`);
    }
  };

  if (getPropertyOrder() + 1 === 0 && propertyList.length > 0) {
    nextProperty();
  }
  const propertyComments = () => {
    if (property?.comments) {
      return (
        <Typography className={classes.commentsAmenitiesFonts}>
          {property.comments}
        </Typography>
      );
    }
    return '';
  };

  const getFlierTitle = (captionName: string | undefined) => {
    // capture everything between the last '/' character and last '_' character
    const result = captionName ?? t('media.flyer');
    return result;
  };

  const getDocumentTitle = (item: PropertyImages, fallbackTitle: string) =>
    item.displayCaption || item.displayName || fallbackTitle;

  const getDocumentExtension = (item: PropertyImages) => {
    const fileName = getDocumentTitle(item, item.mediaType || 'file');
    const extension = fileName.split('.').pop()?.toUpperCase() || '';

    return extension && extension.length <= 5 ? extension : 'PDF';
  };

  const getDocumentFileSize = (fileSize?: number | string) => {
    if (!fileSize) {
      return '';
    }

    if (typeof fileSize === 'string' && /[a-z]/i.test(fileSize)) {
      return fileSize;
    }

    const sizeInBytes = Number(fileSize);
    if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) {
      return '';
    }

    const sizeInMb = sizeInBytes / (1024 * 1024);
    return `${sizeInMb.toFixed(sizeInMb >= 10 ? 0 : 1)} MB`;
  };

  const renderDocumentCard = (
    item: PropertyImages,
    fallbackTitle: string,
    index: number,
  ) => {
    const extension = getDocumentExtension(item);
    const isSheet = ['XLS', 'XLSX', 'CSV'].includes(extension);
    const fileSize = getDocumentFileSize(item.fileSize);

    return (
      <a
        className={classes.attachmentCard}
        href={item.downloadUrl || item.pdf || item.url}
        target="_blank"
        rel="noopener noreferrer"
        title={getDocumentTitle(item, fallbackTitle)}
        key={`${item.id || item.url || fallbackTitle}-${index}`}
      >
        <span
          className={`${classes.attachmentFileIcon} ${
            isSheet ? classes.attachmentFileIconSheet : classes.attachmentFileIconPdf
          }`}
        >
          {extension}
        </span>
        <span>
          <span className={classes.attachmentCardTitle}>
            {getDocumentTitle(item, fallbackTitle)}
          </span>
          {fileSize && (
            <span className={classes.attachmentCardMeta}>{fileSize}</span>
          )}
        </span>
      </a>
    );
  };

  const attachmentsMobileView = () => (
    <Box className={classes.attachmentCardGrid}>
      {flier?.length > 0 &&
        flier.map((item, index) => renderDocumentCard(
          item,
          getFlierTitle(item.displayCaption) || t('media.flyerWithIndex', { index: index + 1 }),
          index,
        ))}
    </Box>
  );

  const shortListClicked = () => {
    if (property) {
      if (!property.metaData) {
        property.metaData = {};
      }
      property.metaData.isClientShortlist = !property.metaData?.isClientShortlist;
      updateInteractiveProperty(survey, property as PropertyPatch, surveyId);
      const targetRef = isLocationMapExpanded ? expandedMapCompRef : inlineMapCompRef;
      updateMapPoints([property], targetRef, survey, '', 15, 1, applyLatestMapStyle);
      forceUpdate();
    }
  };

  const onUpdateSurvey = () => {
    setShortlisted(!shortlisted);
    shortListClicked();
    forceUpdate();
  };

  const onPropertyShortlistClick = () =>
    propertyList.length !== 1 ? shortListClicked() : onUpdateSurvey();

  const getGridClass = (property: any) =>
    activeAvailabilityRules &&
    (property?.availabilities || []).length > 0
      ? ''
      : classes.paddingBottom125;

  const sortedContacts = useMemo(
    () => [...(survey?.contacts || [])].sort((a, b) => {
      const firstOrder = Number.isFinite(a.order) ? Number(a.order) : Number.MAX_SAFE_INTEGER;
      const secondOrder = Number.isFinite(b.order) ? Number(b.order) : Number.MAX_SAFE_INTEGER;
      return firstOrder - secondOrder;
    }),
    [survey?.contacts],
  );

  const getPropertyFieldValueByKey = (fieldKey?: string): string => {
    if (!fieldKey) {
      return '';
    }

    const valueFromProperty = getPropertyValueForDisplay(fieldKey, property as Property);
    if (valueFromProperty && `${valueFromProperty}`.trim()) {
      return `${valueFromProperty}`;
    }

    const valueFromCustomField =
      property?.customFields?.find((field: any) => field.fieldKey === fieldKey)
        ?.value || '';

    return `${valueFromCustomField || ''}`;
  };

  const getDescriptionByLabel = (labelKeyword: string) => {
    const fieldRule = survey?.surveyPropertyFieldRule?.find(
      (rule: any) =>
        rule.status === 'ACTIVE' &&
        `${rule.fieldLabel || ''}`.toLowerCase().includes(labelKeyword),
    );

    return getPropertyFieldValueByKey(fieldRule?.fieldKey);
  };

  const getDeduplicatedAmenities = (): string => {
    const amenityFieldRules = survey?.surveyPropertyFieldRule?.filter(
      (rule: any) =>
        rule.status === 'ACTIVE' &&
        EXTRACTED_PROPERTY_DETAIL_FIELD_IDENTITIES.specificationAmenities.includes(
          normalizeFieldIdentity(rule.fieldKey) as any,
        ),
    ) || [];

    const allAmenityValues = amenityFieldRules
      .map((rule: any) => getPropertyFieldValueByKey(rule.fieldKey))
      .filter(Boolean)
      .join('\n');

    const items = getDisplayListItems(allAmenityValues);
    const uniqueItems = Array.from(
      new Map(items.map((item) => [item.toLowerCase(), item])).values(),
    );

    return uniqueItems.join('\n');
  };

  const getPropertyFieldRuleByIdentity = (identities: readonly string[]) =>
    survey?.surveyPropertyFieldRule?.find((rule: any) => {
      if (rule.status !== 'ACTIVE') {
        return false;
      }

      const normalizedFieldKey = normalizeFieldIdentity(rule.fieldKey);
      const normalizedFieldLabel = normalizeFieldIdentity(rule.fieldLabel);

      return identities.some(
        (identity) =>
          normalizedFieldKey === identity || normalizedFieldLabel === identity,
      );
    });

  const normalizeTextContent = (value: string) =>
    (value || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const renderAmenityList = (value?: string) => {
    const items = getDisplayListItems(value);
    if (items.length === 0) {
      return null;
    }

    return (
      <Box component="ul" className={classes.amenitiesList}>
        {items.map((item, index) => (
          <Box component="li" className={classes.amenityListItem} key={`${item}-${index}`}>
            <span className={classes.amenityDash}>-</span>
            <span>{item}</span>
          </Box>
        ))}
      </Box>
    );
  };

  const renderTransportTiles = (value?: string) => {
    const transportItems = getTransportCardItems(value);

    if (transportItems.length === 0) {
      return null;
    }

    return (
      <Box className={classes.transportGrid}>
        {transportItems.map((item, index) => (
          <Box
            className={`${classes.transportTile} ${
              item.lines.length > 0 ? '' : classes.transportTilePlaceholder
            }`}
            key={`${item.icon}-${index}`}
          >
            <Box className={classes.transportIcon}>
              <TransportIcon icon={item.icon} />
            </Box>
            {item.lines.length > 0 && (
              <Typography className={classes.transportText}>
                {item.lines.map((line, lineIndex) => (
                  <span
                    className={classes.transportTextLine}
                    key={`${item.icon}-${lineIndex}`}
                  >
                    {line}
                  </span>
                ))}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const propertyDescription = useMemo(() => {
    const fromRule = getDescriptionByLabel('property description');
    const fallback = getPropertyFieldValueByKey('description') || property?.comments || '';
    return normalizeTextContent(fromRule || fallback);
  }, [property, survey?.surveyPropertyFieldRule]);

  const locationDescription = useMemo(() => {
    const fromRule = getDescriptionByLabel('location description');
    const fallback = getPropertyFieldValueByKey('locationDescription') || '';
    return normalizeTextContent(fromRule || fallback);
  }, [property, survey?.surveyPropertyFieldRule]);

  const locationTransportFieldRule = useMemo(
    () => getPropertyFieldRuleByIdentity(
      EXTRACTED_PROPERTY_DETAIL_FIELD_IDENTITIES.locationTransport,
    ),
    [survey?.surveyPropertyFieldRule],
  );

  const locationTransportValue = getPropertyFieldValueByKey(
    locationTransportFieldRule?.fieldKey,
  );

  const specificationAmenitiesValue = getDeduplicatedAmenities();

  const specificationAmenitiesContent = renderAmenityList(
    specificationAmenitiesValue,
  );

  const propertyAmenitiesContent = renderAmenityList(property?.amenities);

  const sectionLinks = useMemo(
    () => [
      { id: PROPERTY_SECTION_IDS.overview, label: t('propertyDetail.overview') },
      { id: PROPERTY_SECTION_IDS.availabilities, label: t('availabilities.title') },
      { id: PROPERTY_SECTION_IDS.amenities, label: t('propertyDetail.amenitiesSpecifications') },
      { id: PROPERTY_SECTION_IDS.propertyDetails, label: t('propertyDetail.propertyDetails') },
      { id: PROPERTY_SECTION_IDS.location, label: t('propertyDetail.locationAndTransport') },
      { id: PROPERTY_SECTION_IDS.attachments, label: t('propertyDetail.attachments') },
    ],
    [t],
  );

  useEffect(() => {
    const sectionElements = sectionLinks
      .map((section) => document.getElementById(section.id))
      .filter((sectionElement): sectionElement is HTMLElement => Boolean(sectionElement));

    if (sectionElements.length === 0) {
      return undefined;
    }

    const updateActiveSection = () => {
      const stickyTopOffset = getResponsiveStickyTop();

      if (requestedSectionIdRef.current) {
        const requestedSectionElement = document.getElementById(
          requestedSectionIdRef.current,
        );
        const requestedSectionRect = requestedSectionElement?.getBoundingClientRect();
        const isRequestedSectionVisible =
          requestedSectionRect &&
          requestedSectionRect.bottom > stickyTopOffset &&
          requestedSectionRect.top < window.innerHeight;

        if (isRequestedSectionVisible) {
          setActiveSectionId(requestedSectionIdRef.current);
          return;
        }

        requestedSectionIdRef.current = null;
      }

      const viewportBottom = window.innerHeight + window.scrollY;
      const documentBottom = document.documentElement.scrollHeight;
      const scrollTarget = stickyTopOffset + 48;
      const currentSection = sectionElements.reduce((activeSection, sectionElement) => {
        const sectionTop = sectionElement.getBoundingClientRect().top;
        return sectionTop <= scrollTarget ? sectionElement : activeSection;
      }, sectionElements[0]);

      if (viewportBottom >= documentBottom - 8) {
        const lastSection = sectionElements[sectionElements.length - 1];
        const lastSectionTop = lastSection.getBoundingClientRect().top;

        setActiveSectionId(
          lastSectionTop <= window.innerHeight * 0.65
            ? lastSection.id
            : currentSection.id,
        );
        return;
      }

      setActiveSectionId(currentSection.id);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);

    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [property, sectionLinks]);

  const hasAttachments = flier.length > 0;

  const getContactInitials = (contactName?: string) => {
    if (!contactName) {
      return 'CB';
    }

    const [first = '', second = ''] = contactName
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  };

  const onRequestViewing = () => {
    const targetContact = sortedContacts.find((contact) => contact.email);
    if (!targetContact?.email) {
      return;
    }

    const subject = encodeURIComponent(
      `${t('propertyDetail.requestViewingSubject')} - ${property?.name || addressDisplay}`,
    );
    window.location.href = `mailto:${targetContact.email}?subject=${subject}`;
  };

  const scrollToSection = (sectionId: string) => {
    const sectionElement = document.getElementById(sectionId);

    if (!sectionElement) {
      return;
    }

    setActiveSectionId(sectionId);
    requestedSectionIdRef.current = sectionId;

    const stickyTopOffset = getResponsiveStickyTop();

    const sectionTop =
      sectionElement.getBoundingClientRect().top +
      window.scrollY -
      stickyTopOffset -
      16;

    window.scrollTo({
      top: Math.max(sectionTop, 0),
      behavior: 'smooth',
    });
  };

  const sectionNavigation = () => (
    <Box className={classes.sectionNavContainer}>
      {sectionLinks.map((section) => (
        <button
          type="button"
          key={section.id}
          onClick={() => scrollToSection(section.id)}
          aria-current={activeSectionId === section.id ? 'true' : undefined}
          className={`${classes.sectionNavItem} ${
            activeSectionId === section.id ? classes.sectionNavItemActive : ''
          }`}
        >
          {section.label}
        </button>
      ))}
    </Box>
  );

  const advisorSection = () => {
    if (sortedContacts.length === 0) {
      return null;
    }

    // Enable scroll once the list grows beyond a few rows; on smaller viewports,
    const shouldScrollAdvisors = sortedContacts.length > 3;

    return (
      <Box className={classes.advisorPanel}>
        <Typography variant="h6" className={classes.advisorHeading}>
          {t('propertyDetail.yourCbreAdvisors')}
        </Typography>
        <Box
          className={`${classes.advisorList} ${
            shouldScrollAdvisors ? classes.advisorListScrollable : ''
          }`}
        >
          {sortedContacts.map((contact: SurveyContact, index: number) => (
            <Box
              key={`${contact.contactId || contact.email || contact.name || 'contact'}-${index}`}
              className={classes.advisorRow}
            >
              <Box className={classes.advisorAvatar}>
                {getContactInitials(contact.name)}
              </Box>
              <Box className={classes.advisorInfo}>
                <div className={classes.advisorName}>{contact.name || t('common.notAvailable')}</div>
                {contact.title && (
                  <div className={classes.advisorTitle}>{contact.title}</div>
                )}
                <Box className={classes.advisorMetaGroup}>
                  {contact.phoneNumber && (
                    <a href={`tel:${contact.phoneNumber}`} className={classes.advisorMeta}>
                      {contact.phoneNumber}
                    </a>
                  )}
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className={classes.advisorMeta}>
                      {contact.email}
                    </a>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const overviewSection = () => (
    <Box id={PROPERTY_SECTION_IDS.overview} className={classes.sectionBlock}>
      <Typography variant="h6" className={classes.descriptionLabel}>
        {t('propertyDetail.overview')}
      </Typography>
      <ExpandableDescriptionBlock
        title={t('propertyDetail.propertyDescription')}
        description={propertyDescription}
        emptyState={t('common.notAvailable')}
        viewMoreLabel={t('propertyDetail.viewMore')}
        showLessLabel={t('propertyDetail.showLess')}
        titleClassName={classes.descriptionSubTitle}
        textClassName={classes.descriptionBody}
        actionClassName={classes.descriptionAction}
      />
      <ExpandableDescriptionBlock
        title={t('propertyDetail.locationDescription')}
        description={locationDescription}
        emptyState={t('common.notAvailable')}
        viewMoreLabel={t('propertyDetail.viewMore')}
        showLessLabel={t('propertyDetail.showLess')}
        titleClassName={classes.descriptionSubTitle}
        textClassName={classes.descriptionBody}
        actionClassName={classes.descriptionAction}
      />
    </Box>
  );

  const attachmentsSection = () => (
    <Box
      id={PROPERTY_SECTION_IDS.attachments}
      className={`${classes.sectionBlock} ${classes.attachmentsBlock}`}
    >
      <Typography variant="h6" className={classes.sectionHeading}>
        {t('propertyDetail.attachments')}
      </Typography>
      {hasAttachments ? (
        <div>{attachmentsMobileView()}</div>
      ) : (
        <Typography className={classes.commentsAmenitiesFonts}>
          {t('common.notAvailable')}
        </Typography>
      )}
    </Box>
  );

  const overviewImages = useMemo(
    () => images.slice(0, PROPERTY_PAGE_LAYOUT.maxOverviewImages),
    [images],
  );

  const primaryOverviewImage = overviewImages[0];

  const secondaryOverviewImages = overviewImages.slice(
    1,
    PROPERTY_PAGE_LAYOUT.maxOverviewSecondaryImages + 1,
  );

  const shouldShowOverviewMapTile =
    images.length >= 1 && images.length < PROPERTY_PAGE_LAYOUT.maxOverviewImages;

  const overviewSideTileCount = Math.max(
    0,
    PROPERTY_PAGE_LAYOUT.maxOverviewImages - 1,
  );

  const overviewSideImages = secondaryOverviewImages.slice(
    0,
    shouldShowOverviewMapTile
      ? overviewSideTileCount - 1
      : overviewSideTileCount,
  );

  const hasAdditionalOverviewImages =
    survey.isIncludePhotos && images.length > PROPERTY_PAGE_LAYOUT.maxOverviewImages;

  const hasHeroSideGallery =
    overviewSideImages.length > 0 ||
    shouldShowOverviewMapTile ||
    hasAdditionalOverviewImages;

  const desktopOverviewMapPreviewHeight =
    overviewSideImages.length === 0
      ? PROPERTY_PAGE_LAYOUT.heroHeightDesktop
      : Math.floor(PROPERTY_PAGE_LAYOUT.heroHeightDesktop / 2);

  const getOverviewPreviewWidth = useCallback(() => {
    if (isDesktop) {
      return Math.max(Math.floor(window.innerWidth * 0.35), 340);
    }
    return Math.max(window.innerWidth - 32, 240);
  }, [isDesktop]);

  const getOverviewPreviewHeight = useCallback(() => {
    if (isDesktop) {
      return desktopOverviewMapPreviewHeight;
    }
    return 160;
  }, [isDesktop, desktopOverviewMapPreviewHeight]);

  const scrollToLocationSection = () => scrollToSection(PROPERTY_SECTION_IDS.location);

  const openImageGallery = (preferredImage?: PropertyImages) => {
    const fallbackImage = images.find((img) => img.id !== undefined);
    const targetImageId = preferredImage?.id ?? fallbackImage?.id;

    if (targetImageId === undefined) {
      return;
    }

    setClickedMedia(targetImageId);
    setOpen(true);
  };

  const updateOverviewMapZoom = async (direction: 'in' | 'out') => {
    const mapComponent = overviewMapCompRef?.current;

    if (!mapComponent) {
      return;
    }

    if (direction === 'in' && typeof mapComponent.zoomIn === 'function') {
      mapComponent.zoomIn();
      return;
    }

    if (direction === 'out' && typeof mapComponent.zoomOut === 'function') {
      mapComponent.zoomOut();
      return;
    }

    if (
      typeof mapComponent.getMapZoom === 'function' &&
      typeof mapComponent.updateMapZoom === 'function'
    ) {
      const currentZoom = await mapComponent.getMapZoom();
      mapComponent.updateMapZoom(
        Number(currentZoom || 0) + (direction === 'in' ? 1 : -1),
      );
    }
  };

  const renderOverviewMapPreview = (
    height: number,
    className?: string,
    useFullWidth = false,
  ) => {
    const previewWidth = useFullWidth
      ? Math.max(document.documentElement.clientWidth, 320)
      : getOverviewPreviewWidth();

    return (
    <div
      className={`${classes.heroMapPreviewTile} ${className || ''}`}
      style={{ height: `${height}px` }}
      aria-label={t('propertyDetail.locationAndTransport')}
      role="group"
    >
      <span className={classes.heroMapPreviewCanvas} aria-hidden="true">
        <mapping-component
          vendor="mapbox"
          client-id={BAMWSO2ClientID}
          secret-id={BAMWSO2SecretID}
          ref={setOverviewMapRef}
          width={previewWidth}
          height={height}
        ></mapping-component>
      </span>

      <MapStyles
        onMapStyleChange={onMapStyleChange}
        survey={survey}
        currentStyle={currentMapStyle}
        controlPosition={{ right: 58, bottom: 18, zIndex: 4 }}
      />

      <Box className={classes.mapControlGroup}>
        <Button
          className={classes.mapControlButton}
          onClick={() => updateOverviewMapZoom('in')}
          aria-label="Zoom in"
        >
          <ZoomInIcon />
        </Button>
        <Button
          className={classes.mapControlButton}
          onClick={() => updateOverviewMapZoom('out')}
          aria-label="Zoom out"
        >
          <ZoomOutIcon />
        </Button>
      </Box>

      <Button
        className={classes.getDirectionsButton}
        disabled={!hasValidPropertyCoordinates}
        onClick={openPropertyDirections}
        startIcon={<DirectionsActionIcon />}
      >
        {t('propertyDetail.getDirections')}
      </Button>

      <Button
        className={classes.mapExpandButton}
        onClick={() => setIsLocationMapExpanded(true)}
        aria-label="Expand map"
      >
        <ExpandMapIcon />
      </Button>
    </div>
    );
  };

  useEffect(() => {
    if (!shouldShowOverviewMapTile) {
      return;
    }

    const overviewMap = overviewMapCompRef.current;
    if (!overviewMap?.mapResize) {
      return;
    }

    const previewWidth = getOverviewPreviewWidth();
    const previewHeight = getOverviewPreviewHeight();

    window.setTimeout(() => {
      overviewMap.mapResize(previewWidth, previewHeight);
    }, 140);
  }, [
    shouldShowOverviewMapTile,
    getOverviewPreviewWidth,
    getOverviewPreviewHeight,
    propertyId,
    images.length,
  ]);

  const renderLocationMapContent = (expandedMap = false) => (
    <div className={expandedMap ? classes.locationMapModalBody : undefined}>
      <main
        className={`${classes.content} ${
          expandedMap ? classes.locationMapContentExpanded : ''
        }`}
        ref={expandedMap ? undefined : ref}
      >
        <mapping-component
          vendor="mapbox"
          client-id={BAMWSO2ClientID}
          secret-id={BAMWSO2SecretID}
          ref={expandedMap ? expandedMapCompRef : inlineMapCompRef}
          width={expandedMap ? window.innerWidth : propertyMapDisplayWidth}
          height={expandedMap ? window.innerHeight - 74 : propertyMapDisplayHeight}
        ></mapping-component>
      </main>
      <MapStyles
        onMapStyleChange={onMapStyleChange}
        survey={survey}
        currentStyle={currentMapStyle}
        popoverZIndex={expandedMap ? 2000 : undefined}
        controlPosition={
          expandedMap
            ? { right: 16, bottom: 76, zIndex: 4 }
            : { right: 58, bottom: 18, zIndex: 4 }
        }
      />
      <Box
        className={`${classes.mapControlGroup} ${
          expandedMap ? classes.mapControlGroupExpanded : ''
        }`}
      >
        <Button
          className={classes.mapControlButton}
          onClick={() => updatePropertyMapZoom('in')}
          aria-label="Zoom in"
        >
          <ZoomInIcon />
        </Button>
        <Button
          className={classes.mapControlButton}
          onClick={() => updatePropertyMapZoom('out')}
          aria-label="Zoom out"
        >
          <ZoomOutIcon />
        </Button>
      </Box>
      {!expandedMap && (
        <>
          <Button
            className={classes.getDirectionsButton}
            disabled={!hasValidPropertyCoordinates}
            onClick={openPropertyDirections}
            startIcon={<DirectionsActionIcon />}
          >
            {t('propertyDetail.getDirections')}
          </Button>
          <Button
            className={classes.mapExpandButton}
            onClick={() => setIsLocationMapExpanded(true)}
            aria-label="Expand map"
          >
            <ExpandMapIcon />
          </Button>
        </>
      )}
    </div>
  );

  const renderLocationMap = (expandedMap = false) => {
    if (!expandedMap) {
      return renderLocationMapContent(false);
    }

    return (
      <>
        <Box className={classes.locationMapModalHeader}>
          <Typography className={classes.locationMapModalTitle}>
            {addressDisplay || t('common.notAvailable')}
          </Typography>
          <Button
            className={classes.mapCloseButton}
            onClick={() => setIsLocationMapExpanded(false)}
            aria-label="Close map"
          >
            <CloseMapIcon />
          </Button>
        </Box>
        {renderLocationMapContent(true)}
      </>
    );
  };

  return (
    <>
      {isLocationMapExpanded && (
        <div
          className={classes.locationMapModal}
          role="dialog"
          aria-modal="true"
          aria-label="Expanded map"
        >
          <div className={classes.locationMapModalContent}>
            {renderLocationMap(true)}
          </div>
        </div>
      )}
      {property && (
        <>
          <Grid container className={classes.pageTypographyScope}>
            <Grid item xs={12} sm={12} className={classes.header}>
              <Grid item xs={9} sm={8} md={9} lg={9}>
                <Grid container className={classes.propHeaderTitle}>
                  <Grid
                    item
                    xs={3}
                    sm={1}
                    md={1}
                    lg={1}
                    className={classes.order}
                  >
                    <Typography className={classes.headerSecondaryText}>
                      {getPropertyOrder() + 1}
                    </Typography>
                    <div className={classes.verticalLine} />
                  </Grid>
                  <Grid
                    item
                    xs={9}
                    sm={11}
                    md={11}
                    lg={11}
                    className={classes.headerText}
                  >
                    {property.name !== 'Untitled Property' && (
                      <Typography variant="h4" className={classes.title}>
                        {property.name}
                      </Typography>
                    )}
                    {property.name !== 'Untitled Property' &&
                      property.address1 && (
                      <Box className={classes.headerAddressRow}>
                        <Box className={classes.headerAddressIcon}>
                          <MapPinActionIcon />
                        </Box>
                        <Typography
                          variant="body2"
                          className={classes.addressText}
                        >
                          {addressDisplay}
                        </Typography>
                        <button
                          type="button"
                          className={classes.headerShowMapButton}
                          onClick={scrollToLocationSection}
                        >
                          {t('propertyDetail.showMap')}
                        </button>
                      </Box>
                    )}
                    {property.name === 'Untitled Property' &&
                      property.address1 && (
                      <>
                        <Typography variant="h4" className={classes.title}>
                          {property.address1}
                        </Typography>
                        <Box className={classes.headerAddressRow}>
                          <Box className={classes.headerAddressIcon}>
                            <MapPinActionIcon />
                          </Box>
                          <Typography
                            variant="body2"
                            className={classes.addressText}
                          >
                            {property.city}, {property.stateProvince}{' '}
                            {property.postalCode}
                          </Typography>
                          <button
                            type="button"
                            className={classes.headerShowMapButton}
                            onClick={scrollToLocationSection}
                          >
                            {t('propertyDetail.showMap')}
                          </button>
                        </Box>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={3}
                sm={4}
                md={3}
                lg={3}
                className={classes.navigation}
              >
                {survey?.properties && (
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      className={classes.innerNavigation}
                    >
                      {getPropertyOrder() !== 0 ? (
                        <Button
                          className={classes.paginationButton}
                          onMouseOver={changeArrowBackground}
                          onClick={() => previousProperty()}
                        >
                          <LeftArrowIcon
                            stroke={externalTheme.palette.primary.main}
                          ></LeftArrowIcon>
                        </Button>
                      ) : (
                        <Button
                          className={classes.paginationButton}
                          onMouseOver={changeArrowBackground}
                        >
                          <LeftArrowIcon
                            stroke={externalTheme.palette.grey['600']}
                          ></LeftArrowIcon>
                        </Button>
                      )}

                      {isDesktop ? (
                        <Typography
                          variant="body2"
                          className={classes.indexCount}
                        >
                          {getPropertyOrder() + 1} of {propertyList.length}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          className={classes.indexCount}
                        >
                          {getPropertyOrder() + 1}/{propertyList.length}
                        </Typography>
                      )}

                      {getPropertyOrder() !== propertyList.length - 1 ? (
                        <Button
                          onMouseOver={changeArrowBackground}
                          className={classes.paginationButton}
                          onClick={() => nextProperty()}
                        >
                          <RightArrowIcon
                            stroke={externalTheme.palette.primary.main}
                          ></RightArrowIcon>
                        </Button>
                      ) : (
                        <Button
                          className={classes.paginationButton}
                          onMouseOver={changeArrowBackground}
                        >
                          <RightArrowIcon
                            stroke={externalTheme.palette.grey['600']}
                          ></RightArrowIcon>
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid container className={classes.propertyDetailContainer}>
              <Grid item xs={12} className={classes.heroSection}>
                {isDesktop ? (
                  <Grid container>
                    <Grid
                      item
                      lg={hasHeroSideGallery ? 8 : 12}
                      md={hasHeroSideGallery ? 8 : 12}
                      sm={12}
                      xs={12}
                      className={classes.heroPrimaryColumn}
                    >
                      {imagesFetched && images.length === 0 ? (
                        renderOverviewMapPreview(
                          PROPERTY_PAGE_LAYOUT.heroHeightDesktop,
                          undefined,
                          true,
                        )
                      ) : (
                        <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
                          <img
                            src={
                              primaryOverviewImage
                                ? primaryOverviewImage.url
                                : mediumPlaceholderImg
                            }
                            className={classes.imageStyle}
                            style={{
                              filter: (() => {
                                if (images.length <= 1) return 'contrast(1.35) saturate(1.15) brightness(1.04)';
                                if (images.length <= 2) return 'contrast(1.28) saturate(1.1) brightness(1.02)';
                                return 'contrast(1.2) saturate(1.08)';
                              })(),
                            }}
                            role="button"
                            onLoad={() => {
                              setImageLoaded(true);
                            }}
                            onClick={() => {
                              openImageGallery(primaryOverviewImage);
                            }}
                          />
                          {!imageLoaded && (
                            <Box className={classes.imageLoaderOverlay}>
                              <CircularProgress size={60} />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Grid>
                    {/* SVG element kept for potential future use but filter not applied to hero images */}
                    <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
                      <defs />
                    </svg>
                    {hasHeroSideGallery && (
                      <Grid
                        item
                        lg={4}
                        md={4}
                        sm={12}
                        xs={12}
                        style={{ position: 'relative' }}
                      >
                        <Box className={classes.heroSecondaryColumn}>
                          {overviewSideImages.map((item) => {
                            const imageUrl = item?.url || mediumPlaceholderImg;

                            return (
                              <button
                                type="button"
                                key={item?.id || item?.url}
                                className={classes.heroSecondaryItem}
                                aria-label={t('propertyDetail.viewAllPhotos', { count: images.length })}
                                onClick={() => {
                                  openImageGallery(item);
                                }}
                                style={{
                                  backgroundImage: `url('${imageUrl}')`,
                                  backgroundPosition: 'center center',
                                  backgroundSize: 'cover',
                                  backgroundRepeat: 'no-repeat',
                                }}
                              ></button>
                            );
                          })}
                          {shouldShowOverviewMapTile && (
                            renderOverviewMapPreview(
                              desktopOverviewMapPreviewHeight,
                              classes.heroSecondaryItem,
                            )
                          )}
                        </Box>
                        {hasAdditionalOverviewImages && (
                          <Box className={classes.heroViewAllOverlay}>
                            <Button
                              variant="outlined"
                              className={classes.viewAllButton}
                              onClick={() => openImageGallery(primaryOverviewImage)}
                            >
                              <ViewAllImagesIcon />
                              {t('propertyDetail.viewAllPhotosShort', {
                                count: images.length,
                              })}
                            </Button>
                          </Box>
                        )}
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    {(() => {
                      if (primaryOverviewImage) {
                        return (
                          <Box style={{ position: 'relative', width: '100%', height: '100%', marginBottom: 8 }}>
                            <img
                              src={primaryOverviewImage.url}
                              className={classes.imageStyle}
                              role="button"
                              onLoad={() => {
                                setImageLoaded(true);
                              }}
                              onClick={() => openImageGallery(primaryOverviewImage)}
                            />
                            {!imageLoaded && (
                              <Box className={classes.imageLoaderOverlay}>
                                <CircularProgress size={60} />
                              </Box>
                            )}
                          </Box>
                        );
                      }
                      if (imagesFetched && images.length === 0) {
                        return (
                          <div style={{ marginBottom: 8 }}>
                            {renderOverviewMapPreview(220, undefined, true)}
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{
                            backgroundImage: `url('${mediumPlaceholderImg}')`,
                            marginBottom: 8,
                          }}
                          className={classes.imageStyle}
                        ></div>
                      );
                    })()}
                    {overviewSideImages.map((img) => {
                      const imgId = String(img.id || img.url);
                      const isLoading = secondaryImagesLoading[imgId] !== false;
                      // Preload the image
                      preloadSecondaryImage(imgId, img.url);
                      return (
                        <Box
                          key={imgId}
                          style={{
                            position: 'relative',
                            backgroundImage: `url('${img.url}')`,
                            marginBottom: 8,
                          }}
                          className={classes.secondaryImageStyle}
                          role="button"
                          onClick={() => openImageGallery(img)}
                        >
                          {isLoading && (
                            <Box className={classes.imageLoaderOverlay}>
                              <CircularProgress size={40} />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    {shouldShowOverviewMapTile && (
                      renderOverviewMapPreview(160, classes.secondaryImageStyle)
                    )}
                    {hasAdditionalOverviewImages && (
                      <Box className={classes.overviewActionRow}>
                        <Button
                          variant="outlined"
                          className={classes.viewAllButton}
                          onClick={() => openImageGallery(primaryOverviewImage)}
                        >
                          <ViewAllImagesIcon />
                          {t('propertyDetail.viewAllPhotosShort', {
                            count: images.length,
                          })}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12} className={classes.contentColumns}>
                <Grid container className={classes.contentGrid}>
                  <Grid
                    item
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    className={classes.navColumn}
                  >
                    {sectionNavigation()}
                  </Grid>

                  <Grid
                    ref={propertyDetailsRef}
                    item
                    lg={5}
                    md={5}
                    sm={12}
                    xs={12}
                    className={`${classes.mainColumn} ${getGridClass(property)}`}
                  >
                    {overviewSection()}

                    <Box
                      id={PROPERTY_SECTION_IDS.availabilities}
                      className={`${classes.sectionBlock} ${classes.availabilitySection}`}
                    >
                      {activeAvailabilityRules && (property.availabilities || []).length > 0 ? (
                        <PropertyAvailabilities
                          survey={survey}
                          isCESurvey={isCESurvey}
                          displayFields={activeAvailabilityRules as BHPropertyFieldRule[]}
                          availabilities={property.availabilities as any[]}
                          floorPlans={floorplan}
                        ></PropertyAvailabilities>
                      ) : (
                        <>
                          <Typography variant="h6" className={classes.sectionHeading}>
                            {t('availabilities.title')}
                          </Typography>
                          <Typography className={classes.commentsAmenitiesFonts}>
                            {t('common.notAvailable')}
                          </Typography>
                        </>
                      )}
                    </Box>

                    <Box id={PROPERTY_SECTION_IDS.amenities} className={classes.sectionBlock}>
                      <Typography variant="h6" className={classes.sectionHeading}>
                        {t('propertyDetail.amenitiesSpecifications')}
                      </Typography>
                      {specificationAmenitiesContent}
                      {isAmenitiesVisible && property?.amenities && propertyAmenitiesContent}
                      {!specificationAmenitiesContent &&
                        !(isAmenitiesVisible && property?.amenities) && (
                        <Typography className={classes.commentsAmenitiesFonts}>
                          {t('common.notAvailable')}
                        </Typography>
                      )}
                      {isCommentVisible && property?.comments && (
                        <Box style={{ marginTop: '12px' }}>
                          <Typography variant="body2" className={classes.sectionHeading}>
                            {t('propertyDetail.comments')}
                          </Typography>
                          {propertyComments()}
                        </Box>
                      )}
                    </Box>

                    <Box
                      id={PROPERTY_SECTION_IDS.propertyDetails}
                      className={`${classes.sectionBlock} ${classes.detailsTableSection}`}
                    >
                      <Typography
                        variant="h6"
                        className={classes.sectionHeading}
                      >
                        {t('propertyDetail.propertyDetails')}
                      </Typography>
                      <InteractivePropertyDetail
                        survey={survey}
                        property={property}
                        key={propertyId}
                        excludedFieldIdentities={EXCLUDED_PROPERTY_DETAIL_FIELD_IDENTITIES}
                      />
                    </Box>

                    <Box id={PROPERTY_SECTION_IDS.location} className={classes.sectionBlock}>
                      <Typography variant="h6" className={classes.sectionHeading}>
                        {t('propertyDetail.locationAndTransport')}
                      </Typography>
                      <Typography className={classes.commentsAmenitiesFonts}>
                        {addressDisplay || t('common.notAvailable')}
                      </Typography>
                      <div
                        className={classes.locationMapWrapper}
                      >
                        {!isLocationMapExpanded && renderLocationMap(false)}
                      </div>
                      {renderTransportTiles(locationTransportValue)}
                    </Box>

                    {attachmentsSection()}
                  </Grid>

                  <Grid
                    item
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    className={classes.sideColumn}
                  >
                    <Box className={classes.sectionPaper}>
                      <Button
                        variant="contained"
                        className={classes.requestViewingButton}
                        onClick={onRequestViewing}
                        startIcon={<CalendarActionIcon />}
                      >
                        {t('propertyDetail.requestViewing')}
                      </Button>
                      <PropertySidebarActions
                        propertyId={propertyId}
                        isClientShortlist={
                          property.metaData?.isClientShortlist ?? false
                        }
                        onShortlistClick={onPropertyShortlistClick}
                        shortlistLabel={t('propertyDetail.shortlistProperty')}
                        shortlistedLabel={t('propertyList.shortlisted')}
                        classes={classes}
                      />
                      {advisorSection()}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <InteractiveSurveyPropertyImageModal
                medias={images}
                clickedMedia={clickedMedia}
                open={open}
                setOpen={setOpen}
                surveyCountry={survey?.surveyCountry}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

InteractiveSurveyProperty.displayName = 'InteractiveSurveyProperty';
export default InteractiveSurveyProperty;
